/**
 * PRAYER TRIAL PAGE - Tanzeen+
 * Premium prayer times with calculated methods (FREE) and Local Mosque Times (PRO)
 * - Auto-refresh at 23:59 local time
 * - Caching for extracted mosque data
 * - Full i18n support
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { PageHeader } from '../components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { usePro, PRO_FEATURES } from '../contexts/ProContext';
import { useFortress, FEATURE_KEYS } from '../contexts/FortressContext';
import { useTheme } from '../contexts/ThemeContext';
import { usePrayerSchedule } from '../contexts/PrayerScheduleContext';
import { 
  Clock, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  Sun,
  Sunrise,
  Sunset,
  Moon,
  SunDim,
  Star,
  Building2,
  RefreshCw,
  Info,
  ExternalLink,
  Database,
  Zap,
  Calendar,
  Layers,
  MapPin,
  Search,
  Lock,
  Crown,
  Navigation,
  ChevronDown,
  Shield,
  Radar,
  Globe,
  Locate
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// =============================================================================
// ASYNC V2 EXTRACTION HELPER
// =============================================================================
// The backend /prayer-trial-v2/extract endpoint is asynchronous: it returns
// {job_id} immediately and the frontend polls /extract/status/{job_id} until
// the job is completed or failed. This helper encapsulates that dance so
// individual call sites can keep the previous "await + get result" shape.
//
// - Polls every 3 seconds
// - Persists the active job in localStorage under LS_ACTIVE_JOB_KEY so we can
//   resume polling if the component unmounts / the app is backgrounded.
// - Hard client-side deadline: 120 s (well above the p95 extraction time of
//   ~40 s) — protects UI from a runaway server-side job.
// =============================================================================
const LS_ACTIVE_JOB_KEY = 'tanzeen.prayerV2.activeJob';

const _readActiveJob = () => {
  try {
    const raw = window.localStorage.getItem(LS_ACTIVE_JOB_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.jobId) return null;
    // Ignore anything older than 5 minutes to avoid ghost-resume
    if (Date.now() - (parsed.savedAt || 0) > 5 * 60 * 1000) return null;
    return parsed;
  } catch (_) { return null; }
};

const _writeActiveJob = (jobId, url) => {
  try {
    window.localStorage.setItem(
      LS_ACTIVE_JOB_KEY,
      JSON.stringify({ jobId, url, savedAt: Date.now() })
    );
  } catch (_) {}
};

const _clearActiveJob = () => {
  try { window.localStorage.removeItem(LS_ACTIVE_JOB_KEY); } catch (_) {}
};

/**
 * =============================================================================
 * V5 — Placeholder extraction engine.
 * =============================================================================
 * The V2 async job pipeline (Playwright + Gemini) has been intentionally
 * neutralized on this duplicated page. All calls that would have hit
 *   POST /api/prayer-trial-v2/extract
 *   GET  /api/prayer-trial-v2/extract/status/{job_id}
 * are short-circuited below with a clear log + toast, awaiting the new
 * extraction engine code that will be plugged in here.
 *
 * The UI (steps, inputs, layout, styling) is byte-for-byte identical to
 * PrayerTrialPage.jsx. Only the two helpers below (and one inline copy
 * of the same POST at ~line 874) route through this placeholder.
 * =============================================================================
 */
const _V5_NOT_WIRED = (url) => {
  // Legacy placeholder; kept only in case something still routes here.
  const msg = 'V5 fallback placeholder invoked unexpectedly.';
  console.warn('[V5 placeholder]', msg, { url });
  const err = new Error(msg); err.v5Placeholder = true;
  throw err;
};

// Real caller — hits the new server-side CACHED endpoint (Mongo cache with
// asyncio.Lock mutex + Gemini fallback). See backend/prayer_cache_engine.py.
const _formatToday = () => new Date().toLocaleDateString('en-GB', {
  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
});

const _isoToday = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const pollV2Job = async (_jobId, _opts = {}) => {
  // V5 has no async job flow - callers that still route here get a no-op.
  return { success: false, v5Placeholder: true };
};

const extractV2Async = async ({ url } = {}) => {
  // The prayer-cache/lookup endpoint accepts the same "query" (postcode,
  // mosque name, or URL). It returns a cached row instantly on repeat calls
  // and only invokes Gemini on the FIRST hit of the day per mosque.
  const resp = await fetch(`${API}/prayer-cache/lookup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query: (url || '').trim(), target_date: _isoToday() }),
  });
  if (!resp.ok) {
    let detail = '';
    try { detail = (await resp.json())?.detail || ''; } catch (_) {}
    throw new Error(detail || `Prayer-cache lookup failed (HTTP ${resp.status})`);
  }
  const payload = await resp.json();
  const { success, data, cached, stale, schedule_date: scheduleDate } = payload;
  if (!success || !data) throw new Error('Prayer-cache returned empty payload');

  // Map API schema → shape the existing UI expects (times/jamaah split).
  return {
    success: true,
    mosque_name: data.mosqueName,
    address: data.fullAddress,
    data_source: data.source || 'prayer_cache',
    confidence: 90,
    extracted_count: 6,
    extracted_date: data.dateExtracted,
    cached: !!cached,       // true => served from Mongo cache (0 Gemini cost)
    stale: !!stale,         // true => yesterday's fallback (today's fetch failed)
    schedule_date: scheduleDate || _isoToday(),
    prayers: {
      fajr:    { athan: data.times?.fajr,    iqamah: data.jamaah?.fajr },
      sunrise: { time:  data.sunrise },
      dhuhr:   { athan: data.times?.dhuhr,   iqamah: data.jamaah?.dhuhr },
      asr:     { athan: data.times?.asr,     iqamah: data.jamaah?.asr },
      maghrib: { athan: data.times?.maghrib, iqamah: data.jamaah?.maghrib },
      isha:    { athan: data.times?.isha,    iqamah: data.jamaah?.isha },
      jumuah:  { salah: data.jumuah },
    },
  };
};

// Local storage key for cached mosque data
const MOSQUE_CACHE_KEY = 'tanzeel_mosque_prayer_cache';

// Cache version - increment this to force cache refresh when extraction logic changes
const CACHE_VERSION = '2.0_daily_date';  // Updated: constrained date extraction

// Calculation Methods for Prayer Times
const CALCULATION_METHODS = [
  { id: 15, name: 'Moonsighting Committee (UK)', description: 'Recommended for UK' },
  { id: 2, name: 'Islamic Society of North America (ISNA)', description: 'North America' },
  { id: 1, name: 'Muslim World League (MWL)', description: 'Europe, Far East' },
  { id: 4, name: 'Umm Al-Qura University', description: 'Saudi Arabia' },
  { id: 3, name: 'Egyptian General Authority', description: 'Africa, Syria, Lebanon' },
  { id: 5, name: 'University of Islamic Sciences, Karachi', description: 'Pakistan, Bangladesh' },
  { id: 7, name: 'Institut Géophysique du Globe de Paris', description: 'France' },
  { id: 8, name: 'Gulf Region', description: 'UAE, Kuwait, Qatar' },
  { id: 9, name: 'Kuwait', description: 'Kuwait' },
  { id: 10, name: 'Qatar', description: 'Qatar' },
  { id: 11, name: 'Singapore', description: 'Singapore' },
  { id: 12, name: 'Turkey (Diyanet)', description: 'Turkey' },
  { id: 13, name: 'Tehran (Shia)', description: 'Iran' },
];

// Prayer icon colors matching the premium design
// Each prayer has a UNIQUE icon representing its time of day
const PRAYER_STYLES = {
  fajr: { 
    icon: Star,  // Pre-dawn twilight - stars still visible
    bgGradient: 'bg-gradient-to-br from-indigo-500 to-blue-600',
    labelKey: 'fajr',
  },
  sunrise: { 
    icon: Sunrise,  // Sun peeking over the horizon
    bgGradient: 'bg-gradient-to-br from-orange-400 to-amber-500',
    labelKey: 'sunrise',
  },
  dhuhr: { 
    icon: Sun,  // Sun at its zenith (high noon)
    bgGradient: 'bg-gradient-to-br from-yellow-400 to-orange-500',
    labelKey: 'dhuhr',
  },
  asr: { 
    icon: SunDim,  // Sun lowering/angled (mid-afternoon)
    bgGradient: 'bg-gradient-to-br from-orange-400 to-orange-600',
    labelKey: 'asr',
  },
  maghrib: { 
    icon: Sunset,  // Sunset silhouette
    bgGradient: 'bg-gradient-to-br from-pink-500 to-red-500',
    labelKey: 'maghrib',
  },
  isha: { 
    icon: Moon, 
    bgGradient: 'bg-gradient-to-br from-purple-400 to-purple-600',
    labelKey: 'isha',
  },
  jumuah: { 
    icon: Layers, 
    bgGradient: 'bg-gradient-to-br from-yellow-400 to-orange-500',
    labelKey: 'jumuah',
  }
};

// Determine which prayer is "Up Next" based on current time
const getUpNextPrayer = (prayers) => {
  if (!prayers) return null;
  
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  
  const prayerOrder = ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'];
  
  for (const prayerName of prayerOrder) {
    const prayer = prayers[prayerName];
    const timeStr = prayer?.time || prayer?.jamaah || prayer?.start;
    if (!timeStr) continue;
    
    const timePart = timeStr.split(' ')[0];
    const [hours, minutes] = timePart.split(':').map(Number);
    const prayerMinutes = hours * 60 + minutes;
    
    if (prayerMinutes > currentMinutes) {
      return prayerName;
    }
  }
  
  return 'fajr';
};

// Helper to format URL to a readable mosque name
const formatUrlToMosqueName = (url) => {
  try {
    const hostname = new URL(url).hostname;
    return hostname
      .replace(/^www\./, '')
      .replace(/\.(com|org|co\.uk|org\.uk|net|io|uk)$/, '')
      .replace(/[-_]/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  } catch {
    return 'Unknown Mosque';
  }
};

// Get today's date as a string for cache validation
const getTodayDateString = () => {
  const today = new Date();
  return `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
};

// Load cached mosque data
const loadCachedMosqueData = (url) => {
  try {
    const cached = localStorage.getItem(MOSQUE_CACHE_KEY);
    if (!cached) return null;
    
    const data = JSON.parse(cached);
    const today = getTodayDateString();
    
    // Check cache version - invalidate old format caches
    if (data.version !== CACHE_VERSION) {
      console.log('[Tanzeen+] Cache version mismatch, clearing old cache');
      localStorage.removeItem(MOSQUE_CACHE_KEY);
      return null;
    }
    
    // Check if cache is for today and same URL
    if (data.date === today && data.url === url) {
      return data.result;
    }
    return null;
  } catch {
    return null;
  }
};

// Save mosque data to cache
const saveMosqueDataToCache = (url, result) => {
  try {
    const cacheData = {
      version: CACHE_VERSION,  // Include version for future cache invalidation
      url,
      date: getTodayDateString(),
      result,
      timestamp: Date.now()
    };
    localStorage.setItem(MOSQUE_CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.error('Failed to cache mosque data:', error);
  }
};

// =============================================================================
// PREMIUM PRAYER CARD COMPONENT
// =============================================================================
const PrayerCard = ({ prayerName, prayer, isUpNext, isFriday, isCalculated, t, themeColors, appTheme, isDark }) => {
  const style = PRAYER_STYLES[prayerName] || PRAYER_STYLES.dhuhr;
  const Icon = style.icon;
  
  const displayTime = prayer?.time || prayer?.jamaah || prayer?.start || null;
  const athanTime = prayer?.start || null;
  const jamaahTime = prayer?.jamaah || null;
  
  // Get translated prayer name
  const prayerLabel = t(`prayerTrial.prayers.${style.labelKey}`);
  
  // Theme-specific card styles with Night Vision support
  const getCardStyle = () => {
    if (appTheme === 'classic') {
      return isDark ? {
        // Classic Night Vision - Dark slate
        background: 'rgba(30, 41, 59, 0.95)',
        border: '2px solid rgba(52, 211, 153, 0.4)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
        textPrimary: '#f1f5f9',
        textSecondary: '#94a3b8',
        iconBg: 'linear-gradient(135deg, #34d399 0%, #059669 100%)',
        calendarBg: 'rgba(52, 211, 153, 0.25)',
        calendarColor: '#34d399',
      } : {
        // Classic Day - Cream cards with DARK GREEN icons
        background: '#F1EBDD',
        border: '1px solid #C9A227',
        boxShadow: '0 2px 8px rgba(139, 119, 66, 0.1)',
        textPrimary: '#0F4C3A',
        textSecondary: '#4A4A4A',
        iconBg: 'linear-gradient(135deg, #0F4C3A 0%, #125845 100%)',
        calendarBg: 'rgba(15, 76, 58, 0.15)',
        calendarColor: '#0F4C3A',
      };
    }
    if (appTheme === 'royal') {
      return isDark ? {
        // Royal Night Vision - Dark purple
        background: 'rgba(30, 26, 50, 0.95)',
        border: '2px solid rgba(201, 162, 39, 0.5)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
        borderRadius: '16px',
        textPrimary: '#ffffff',
        textSecondary: '#d4af37',
        iconBg: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
        iconBorder: '2px solid rgba(201, 162, 39, 0.6)',
        calendarBg: 'rgba(139, 92, 246, 0.25)',
        calendarColor: '#a78bfa',
      } : {
        // Royal Day - Cream cards with GOLD BORDER + PURPLE icons
        background: '#F1EBDD',
        border: '2px solid #c9a227',
        boxShadow: '0 2px 8px rgba(201, 162, 39, 0.2)',
        borderRadius: '16px',
        textPrimary: '#1a1a1a',
        textSecondary: '#4a4a4a',
        iconBg: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
        iconBorder: '2px solid #c9a227',
        calendarBg: 'rgba(139, 92, 246, 0.15)',
        calendarColor: '#8B5CF6',
      };
    }
    if (appTheme === 'emerald') {
      return isDark ? {
        // Emerald Night Vision - Dark forest
        background: 'rgba(15, 40, 32, 0.95)',
        border: '2px solid rgba(16, 185, 129, 0.5)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
        textPrimary: '#ffffff',
        textSecondary: '#6ee7b7',
        iconBg: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
        calendarBg: 'rgba(16, 185, 129, 0.25)',
        calendarColor: '#34d399',
      } : {
        // Emerald Day - Rich green tones with gold accents
        background: 'linear-gradient(135deg, #064E3B 0%, #065F46 100%)',
        border: '1px solid #10B981',
        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)',
        textPrimary: '#ECFDF5',
        textSecondary: '#A7F3D0',
        iconBg: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
        calendarBg: 'rgba(250, 204, 21, 0.2)',
        calendarColor: '#FACC15',
      };
    }
    if (appTheme === 'kids') {
      return isDark ? {
        // Kids Night Vision - Deep indigo
        background: 'rgba(49, 46, 129, 0.95)',
        border: '2px solid rgba(129, 140, 248, 0.5)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
        textPrimary: '#e0e7ff',
        textSecondary: '#c4b5fd',
        iconBg: 'linear-gradient(135deg, #818cf8 0%, #6366f1 100%)',
        calendarBg: 'rgba(251, 191, 36, 0.25)',
        calendarColor: '#fbbf24',
      } : {
        // Kids Day - Fun pink with cyan icons
        background: 'linear-gradient(135deg, #FDF2F8 0%, #FCE7F3 100%)',
        border: '1px solid #EC4899',
        boxShadow: '0 4px 12px rgba(236, 72, 153, 0.2)',
        textPrimary: '#831843',
        textSecondary: '#BE185D',
        iconBg: 'linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)',
        calendarBg: 'rgba(236, 72, 153, 0.2)',
        calendarColor: '#EC4899',
      };
    }
    // Default with dark mode support
    return isDark ? {
      background: 'rgba(26, 26, 46, 0.95)',
      border: '2px solid rgba(201, 162, 39, 0.4)',
      boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
      textPrimary: '#ffffff',
      textSecondary: 'rgba(255,255,255,0.7)',
      iconBg: style.bgGradient,
      calendarBg: 'rgba(201, 162, 39, 0.25)',
      calendarColor: '#d4af37',
    } : {
      background: '#F1EBDD',
      border: '1px solid #c9a227',
      boxShadow: '0 2px 8px rgba(201, 162, 39, 0.15)',
      textPrimary: '#1a1a1a',
      textSecondary: '#4a4a4a',
      iconBg: style.bgGradient,
      calendarBg: 'rgba(201, 162, 39, 0.2)',
      calendarColor: '#c9a227',
    };
  };
  
  const cardStyle = getCardStyle();
  
  // Use tanzeen-card class for Royal Night theme to get decorative corners
  const isRoyalTheme = appTheme === 'royal';
  
  return (
    <div 
      className={`relative p-4 transition-all ${isRoyalTheme ? 'tanzeen-card' : 'rounded-2xl'} ${isFriday ? 'border-t-4' : ''}`}
      style={isRoyalTheme ? {
        borderTopColor: isFriday ? (themeColors?.accent || '#f59e0b') : undefined,
      } : { 
        background: cardStyle.background,
        border: cardStyle.border,
        boxShadow: cardStyle.boxShadow,
        borderTopColor: isFriday ? (themeColors?.accent || '#f59e0b') : undefined,
      }}
      data-testid={`prayer-card-${prayerName}`}
    >
      {/* Corner decorations for Royal Night theme */}
      {isRoyalTheme && (
        <>
          <div className="card-corner-tl" />
          <div className="card-corner-tr" />
          <div className="card-corner-bl" />
          <div className="card-corner-br" />
        </>
      )}
      <div className="flex items-center justify-between relative z-10">
        <div className="flex items-center gap-3">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg"
            style={{ 
              background: cardStyle.iconBg,
              border: cardStyle.iconBorder || 'none'
            }}
          >
            <Icon className="w-6 h-6 text-white" />
          </div>
          
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium" style={{ color: cardStyle.textPrimary }}>
                {prayerLabel}
              </span>
              <div 
                className="w-5 h-5 rounded flex items-center justify-center"
                style={{ background: cardStyle.calendarBg }}
              >
                <Calendar className="w-3 h-3" style={{ color: cardStyle.calendarColor }} />
              </div>
              {isUpNext && (
                <Badge 
                  className="text-xs px-2 py-0.5 rounded-full"
                  style={{ background: themeColors?.accentGradient, color: '#fff' }}
                >
                  {t('prayerTrial.upNext')}
                </Badge>
              )}
              {isFriday && (
                <Badge 
                  className="text-xs px-2 py-0.5 rounded-full"
                  style={{ background: themeColors?.accent || '#f59e0b', color: '#fff' }}
                >
                  {t('prayerTrial.friday')}
                </Badge>
              )}
            </div>
            <p className="text-sm" style={{ color: cardStyle.textSecondary }}>
              {isCalculated ? prayerLabel : (jamaahTime ? t('prayerTrial.jamaah') : prayerLabel)}
            </p>
          </div>
        </div>
        
        <div className="text-right relative z-10">
          {isCalculated && displayTime && (
            <p className="text-xl font-bold" style={{ color: cardStyle.textPrimary }}>
              {displayTime.split(' ')[0]}
            </p>
          )}
          
          {!isCalculated && (
            <>
              {athanTime && (
                <p className="text-sm" style={{ color: cardStyle.textSecondary }}>
                  {athanTime}
                </p>
              )}
              {jamaahTime && (
                <p className="text-xl font-bold" style={{ color: cardStyle.textPrimary }}>
                  {jamaahTime}
                </p>
              )}
              {!jamaahTime && displayTime && (
                <p className="text-xl font-bold" style={{ color: cardStyle.textPrimary }}>
                  {displayTime}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================
const PrayerTrialPage = () => {
  const { t } = useTranslation();
  // V5 is now the default prayer-time page. Real Pro gating restored.
  const { isPro, checkProGate, recordUsage, promptUpgrade, isFeatureUnlockedViaAd, requestFeatureAccess, consumeFeatureUseAction, getFeatureUnlockStatus } = usePro();
  const { appTheme, mode: themeMode } = useTheme();
  
  // 🏰 FORTRESS: Backend-driven access control
  const { 
    validateAccess, 
    isFeatureEnabled, 
    consumeTokens: fortressConsumeTokens,
    isLoaded: fortressLoaded
  } = useFortress();
  
  // Check if Mosque Sync feature is globally enabled
  const isMosqueSyncEnabled = isFeatureEnabled(FEATURE_KEYS.MOSQUE_SYNC);
  
  const isDark = themeMode === 'dark';
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

  // ============================================
  // THEME COLORS - Night Vision Support
  // Dark mode: dark cards with bright text
  // Light mode: cream cards with dark text
  // ============================================
  const getThemeColors = () => {
    if (appTheme === 'royal') {
      return {
        pageBg: isDark 
          ? 'linear-gradient(180deg, #0a0f1f 0%, #0d1326 50%, #080c1a 100%)'
          : 'linear-gradient(180deg, #1e2a4a 0%, #1a2540 50%, #141c32 100%)',
        // Cards: dark purple in night, cream in day
        cardBg: isDark ? 'rgba(30, 26, 50, 0.95)' : '#F1EBDD',
        cardBorder: isDark ? '2px solid rgba(201, 162, 39, 0.4)' : '2px solid #c9a227',
        textPrimary: isDark ? '#ffffff' : '#1a1a1a',
        textSecondary: isDark ? '#d4af37' : '#4a4a4a',
        textOnPage: '#ffffff',
        textOnPageSecondary: isDark ? '#d4af37' : 'rgba(255,255,255,0.7)',
        accent: '#c9a227',
        accentGradient: 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)',
        inputBg: isDark ? '#1a2540' : '#F1EBDD',
        inputBorder: '#c9a227',
        badgeBg: isDark ? 'rgba(201, 162, 39, 0.3)' : 'rgba(201, 162, 39, 0.2)',
        badgeText: isDark ? '#d4af37' : '#c9a227',
      };
    }
    if (appTheme === 'emerald') {
      return {
        pageBg: isDark 
          ? 'hsl(var(--background))'
          : '#064E3B',
        // Cards: dark forest in night, cream in day
        cardBg: isDark ? 'rgba(15, 40, 32, 0.95)' : '#F1EBDD',
        cardBorder: isDark ? '2px solid rgba(16, 185, 129, 0.4)' : '2px solid #c9a227',
        textPrimary: isDark ? '#ffffff' : '#1a1a1a',
        textSecondary: isDark ? '#6ee7b7' : '#4a4a4a',
        textOnPage: '#ffffff',
        textOnPageSecondary: isDark ? '#6ee7b7' : 'rgba(255,255,255,0.8)',
        accent: isDark ? '#10b981' : '#facc15',
        accentGradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        inputBg: isDark ? '#052e21' : '#F1EBDD',
        inputBorder: isDark ? '#10b981' : '#c9a227',
        badgeBg: isDark ? 'rgba(16, 185, 129, 0.25)' : 'rgba(16, 185, 129, 0.15)',
        badgeText: isDark ? '#34d399' : '#059669',
      };
    }
    if (appTheme === 'classic') {
      return {
        pageBg: isDark 
          ? 'linear-gradient(180deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)'
          : '#F1EBDD',
        // Cards: dark slate in night, cream in day
        cardBg: isDark ? 'rgba(30, 41, 59, 0.95)' : '#F1EBDD',
        cardBorder: isDark ? '2px solid rgba(52, 211, 153, 0.4)' : '2px solid #c9a227',
        textPrimary: isDark ? '#f1f5f9' : '#0F4C3A',
        textSecondary: isDark ? '#94a3b8' : '#4a4a4a',
        textOnPage: isDark ? '#f1f5f9' : '#0F4C3A',
        textOnPageSecondary: isDark ? '#94a3b8' : '#4a4a4a',
        accent: isDark ? '#34d399' : '#0F4C3A',
        accentGradient: isDark 
          ? 'linear-gradient(135deg, #34d399 0%, #059669 100%)' 
          : 'linear-gradient(135deg, #0F4C3A 0%, #125845 100%)',
        inputBg: isDark ? '#1e293b' : '#F1EBDD',
        inputBorder: isDark ? '#34d399' : '#c9a227',
        badgeBg: isDark ? 'rgba(52, 211, 153, 0.25)' : 'rgba(15, 76, 58, 0.15)',
        badgeText: isDark ? '#34d399' : '#0F4C3A',
      };
    }
    if (appTheme === 'kids') {
      return {
        pageBg: isDark 
          ? 'linear-gradient(180deg, #1e1b4b 0%, #312e81 50%, #1e1b4b 100%)'
          : '#FDF2F8',
        // Cards: deep indigo in night, cream in day
        cardBg: isDark ? 'rgba(49, 46, 129, 0.95)' : '#F1EBDD',
        cardBorder: isDark ? '2px solid rgba(129, 140, 248, 0.4)' : '2px solid #c9a227',
        textPrimary: isDark ? '#e0e7ff' : '#1a1a1a',
        textSecondary: isDark ? '#c4b5fd' : '#4a4a4a',
        textOnPage: isDark ? '#e0e7ff' : '#4C1D95',
        textOnPageSecondary: isDark ? '#c4b5fd' : '#9333ea',
        accent: isDark ? '#fbbf24' : '#DB2777',
        accentGradient: isDark 
          ? 'linear-gradient(135deg, #818cf8 0%, #6366f1 100%)' 
          : 'linear-gradient(135deg, #DB2777 0%, #be185d 100%)',
        inputBg: isDark ? '#312e81' : '#F1EBDD',
        inputBorder: isDark ? '#818cf8' : '#c9a227',
        badgeBg: isDark ? 'rgba(251, 191, 36, 0.25)' : 'rgba(219, 39, 119, 0.15)',
        badgeText: isDark ? '#fbbf24' : '#DB2777',
      };
    }
    // Default fallback
    return {
      pageBg: isDark 
        ? 'linear-gradient(180deg, #1a1a2e 0%, #16162a 50%, #0f0f1a 100%)'
        : '#475569',
      cardBg: isDark ? 'rgba(26, 26, 46, 0.95)' : '#F1EBDD',
      cardBorder: isDark ? '2px solid rgba(201, 162, 39, 0.4)' : '2px solid #c9a227',
      textPrimary: isDark ? '#ffffff' : '#1a1a1a',
      textSecondary: isDark ? 'rgba(255,255,255,0.7)' : '#4a4a4a',
      textOnPage: isDark ? '#ffffff' : '#1a1a2e',
      textOnPageSecondary: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
      accent: '#c9a227',
      accentGradient: 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)',
      inputBg: isDark ? '#1a1a2e' : '#F1EBDD',
      inputBorder: '#c9a227',
      badgeBg: isDark ? 'rgba(201, 162, 39, 0.25)' : 'rgba(201, 162, 39, 0.15)',
      badgeText: isDark ? '#d4af37' : '#c9a227',
    };
  };

  const themeColors = getThemeColors();
  
  // Check if user has Pro access to Mosque Finder feature
  // For Ad-unlocked users, also check remaining uses
  const mosqueStatus = getFeatureUnlockStatus('mosque_finder');
  const mosqueStatusAlt = getFeatureUnlockStatus('smart_mosque_connect');
  const mosqueStatusAlt2 = getFeatureUnlockStatus('prayer_extraction');
  const activeMosqueStatus = mosqueStatus.unlocked ? mosqueStatus : (mosqueStatusAlt.unlocked ? mosqueStatusAlt : mosqueStatusAlt2);
  const hasMosqueAccess = isPro || (activeMosqueStatus.unlocked && (activeMosqueStatus.usesRemaining === undefined || activeMosqueStatus.usesRemaining > 0));

  // Mode: 'calculated' (FREE) or 'mosque' (PRO)
  const [mode, setMode] = useState('calculated');
  
  // Location state
  const [citySearch, setCitySearch] = useState('');
  const [location, setLocation] = useState(null);
  const [locationName, setLocationName] = useState('');
  const [loadingLocation, setLoadingLocation] = useState(false);
  
  // Calculation method
  const [calculationMethod, setCalculationMethod] = useState(15);
  const [showMethodDropdown, setShowMethodDropdown] = useState(false);
  
  // Calculated prayer times (FREE)
  const [calculatedTimes, setCalculatedTimes] = useState(null);
  const [loadingCalculated, setLoadingCalculated] = useState(false);
  
  // Mosque extraction (PRO)
  const [mosqueUrlInput, setMosqueUrlInput] = useState('');
  const [mosqueExtracting, setMosqueExtracting] = useState(false);
  const [mosqueResult, setMosqueResult] = useState(null);
  const [usingCachedData, setUsingCachedData] = useState(false);
  const [findingNearbyMosque, setFindingNearbyMosque] = useState(false);
  
  // Mosque Discovery Usage (server-confirmed 5/day limit)
  const [mosqueSearchRemaining, setMosqueSearchRemaining] = useState(5);
  
  // Last Updated timestamp for cache-buster verification
  const [lastUpdated, setLastUpdated] = useState(null);

  // Manual Search (Postcode/Name)
  const [manualSearchQuery, setManualSearchQuery] = useState('');
  const [isManualSearching, setIsManualSearching] = useState(false);

  // My Masjids (PRO feature - MongoDB backed)
  const [savedMasjids, setSavedMasjids] = useState([]);
  const [loadingSavedMasjids, setLoadingSavedMasjids] = useState(false);
  const [savingMasjid, setSavingMasjid] = useState(false);

  // LocalStorage keys for persistence
  const STORAGE_KEYS = {
    MODE: 'tanzeel_prayer_mode',
    MOSQUE_RESULT: 'tanzeel_mosque_result',
    MOSQUE_URL: 'tanzeel_mosque_url',
    CACHE_VERSION: 'tanzeel_cache_version',
  };

  // Load saved mosque data on mount + demand-based refresh
  //
  // Strategy per product spec (2026-02-13):
  //   1. Load whatever's cached on this device.
  //   2. If the cache was extracted for a schedule_date OLDER than today,
  //      silently call extractV2Async ONE time. That call hits
  //      /api/prayer-cache/lookup which returns instantly from the shared
  //      server-side Mongo cache when any other user already extracted
  //      today's data for this mosque - so this costs 0 Gemini bills in
  //      the common case.
  //   3. No timers, no cron, no 23:59 or 1 AM triggers - if the user does
  //      not open this page for a week, nothing runs for that week.
  useEffect(() => {
    try {
      // Check cache version - clear old caches if version mismatch
      const savedVersion = localStorage.getItem(STORAGE_KEYS.CACHE_VERSION);
      if (savedVersion !== CACHE_VERSION) {
        console.log('[Tanzeen+] Cache version updated, clearing old prayer data');
        localStorage.removeItem(STORAGE_KEYS.MODE);
        localStorage.removeItem(STORAGE_KEYS.MOSQUE_RESULT);
        localStorage.removeItem(STORAGE_KEYS.MOSQUE_URL);
        localStorage.removeItem(MOSQUE_CACHE_KEY);
        localStorage.setItem(STORAGE_KEYS.CACHE_VERSION, CACHE_VERSION);
        return; // Don't load stale data
      }

      const savedMode = localStorage.getItem(STORAGE_KEYS.MODE);
      const savedMosqueResult = localStorage.getItem(STORAGE_KEYS.MOSQUE_RESULT);
      const savedMosqueUrl = localStorage.getItem(STORAGE_KEYS.MOSQUE_URL);

      if (savedMode === 'mosque' && savedMosqueResult) {
        const parsedResult = JSON.parse(savedMosqueResult);
        if (parsedResult?.success && parsedResult?.prayers) {
          setMosqueResult(parsedResult);
          setMode('mosque');
          setUsingCachedData(true);
          if (parsedResult.lastUpdated) setLastUpdated(parsedResult.lastUpdated);
          if (savedMosqueUrl) setMosqueUrlInput(savedMosqueUrl);

          // ---- Demand-based freshness check ----
          const isoToday = (() => {
            const d = new Date();
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          })();
          const cachedDate = parsedResult.scheduleDate || null;
          if (savedMosqueUrl && cachedDate !== isoToday) {
            console.log(
              '[Tanzeen+] Client cache is from',
              cachedDate,
              '- silently refreshing via shared server cache for',
              isoToday,
            );
            // Fire-and-forget. Hits the shared server cache first; only pays
            // a Gemini bill if no other user has extracted today for this
            // mosque (mutex ensures at most one extraction globally).
            extractV2Async({ url: savedMosqueUrl })
              .then((data) => {
                if (!data?.success || !data?.prayers) return;
                const refreshTimestamp = new Date().toISOString();
                const result = {
                  success: true,
                  mosqueName: data.mosque_name || savedMosqueUrl,
                  address: data.address,
                  prayers: {
                    fajr:    { start: data.prayers?.fajr?.athan,    jamaah: data.prayers?.fajr?.iqamah },
                    sunrise: { time:  data.prayers?.sunrise?.time },
                    dhuhr:   { start: data.prayers?.dhuhr?.athan,   jamaah: data.prayers?.dhuhr?.iqamah },
                    asr:     { start: data.prayers?.asr?.athan,     jamaah: data.prayers?.asr?.iqamah },
                    maghrib: { start: data.prayers?.maghrib?.athan, jamaah: data.prayers?.maghrib?.iqamah },
                    isha:    { start: data.prayers?.isha?.athan,    jamaah: data.prayers?.isha?.iqamah },
                    jumuah:  data.prayers?.jumuah?.salah,
                  },
                  extractedDate: data.extracted_date || null,
                  scheduleDate: data.schedule_date || isoToday,
                  cached: !!data.cached,
                  stale: !!data.stale,
                  lastUpdated: refreshTimestamp,
                };
                setMosqueResult(result);
                setLastUpdated(refreshTimestamp);
                setUsingCachedData(false);
                localStorage.setItem(STORAGE_KEYS.MOSQUE_RESULT, JSON.stringify(result));
                console.log(
                  '[Tanzeen+] Demand refresh done. server_cached=%s stale=%s',
                  data.cached, data.stale,
                );
              })
              .catch((err) => {
                console.warn(
                  '[Tanzeen+] Demand refresh silent failure (keeping cached times):',
                  err?.message,
                );
              });
          }
        }
      }
    } catch (error) {
      console.error('Error loading saved mosque data:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save mosque data when extraction succeeds
  useEffect(() => {
    if (mosqueResult?.success && mode === 'mosque') {
      try {
        localStorage.setItem(STORAGE_KEYS.MODE, 'mosque');
        localStorage.setItem(STORAGE_KEYS.MOSQUE_RESULT, JSON.stringify(mosqueResult));
        if (mosqueUrlInput) {
          localStorage.setItem(STORAGE_KEYS.MOSQUE_URL, mosqueUrlInput);
        }
      } catch (error) {
        console.error('Error saving mosque data:', error);
      }
    }
  }, [mosqueResult, mode, mosqueUrlInput]);
  // Clear saved mosque data when switching to calculated mode
  const clearSavedMosqueData = () => {
    try {
      localStorage.removeItem(STORAGE_KEYS.MODE);
      localStorage.removeItem(STORAGE_KEYS.MOSQUE_RESULT);
      localStorage.removeItem(STORAGE_KEYS.MOSQUE_URL);
    } catch (error) {
      console.error('Error clearing saved mosque data:', error);
    }
  };

  // Check if today is Friday
  const isFriday = new Date().getDay() === 5;
  
  // Determine which times to display
  const displayTimes = mode === 'mosque' && mosqueResult?.success 
    ? mosqueResult.prayers 
    : calculatedTimes;
  
  const upNextPrayer = displayTimes ? getUpNextPrayer(displayTimes) : null;

  // Publish the CURRENTLY DISPLAYED prayer times to the shared React Context
  // (PrayerScheduleContext) so every header / widget across the app mirrors
  // exactly what this page shows - whether the user is in calculated or
  // live-sync mosque mode. The context also writes to localStorage so the
  // header hydrates instantly on cold reloads of secondary pages.
  const { setActiveSchedule } = usePrayerSchedule();
  useEffect(() => {
    if (!displayTimes) return;
    setActiveSchedule({
      activeMode: mode === 'mosque' ? 'MOSQUE_EXTRACTION' : 'CALCULATION',
      activeTimetable: displayTimes,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayTimes, mode]);


  // Get current location on mount
  useEffect(() => {
    getCurrentLocation();
  }, []);

  // V5: resume-on-mount is disabled since the extraction engine is not wired.
  // Any stale job from the old V2 flow is silently cleared so users don't see
  // an error toast when they open the V5 page.
  useEffect(() => {
    try { _clearActiveJob(); } catch (_) {}
  }, []);

  // Handle hash navigation - scroll to Masjid Connect section when coming from menu
  useEffect(() => {
    if (window.location.hash === '#masjid-connect') {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const element = document.getElementById('masjid-connect');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  }, []);

  // Fetch prayer times when location or method changes
  useEffect(() => {
    if (location && mode === 'calculated') {
      fetchCalculatedTimes();
    }
  }, [location, calculationMethod, mode]);

  // Get user's current location
  const getCurrentLocation = async () => {
    setLoadingLocation(true);
    try {
      if (navigator.geolocation) {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { 
            timeout: 30000,           // 30 seconds timeout
            enableHighAccuracy: false, // Use Wi-Fi/Cell towers as fallback
            maximumAge: 60000          // Accept 1-minute old cached location
          });
        });
        
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;
        setLocation({ lat, lon });
        
        try {
          const geoResponse = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`
          );
          const geoData = await geoResponse.json();
          setLocationName(geoData.city || geoData.locality || t('prayerTrial.yourLocation'));
        } catch {
          setLocationName(t('prayerTrial.yourLocation'));
        }
        
        toast.success(t('prayerTrial.locationDetected'));
      }
    } catch (error) {
      console.error('Location error:', error);
      toast.error(t('prayerTrial.locationError'));
    }
    setLoadingLocation(false);
  };

  // Search for a city
  const searchCity = async () => {
    if (!citySearch.trim()) {
      toast.error(t('prayerTrial.enterCityName'));
      return;
    }
    
    setLoadingLocation(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(citySearch)}&limit=1`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        const result = data[0];
        setLocation({ lat: parseFloat(result.lat), lon: parseFloat(result.lon) });
        setLocationName(result.display_name.split(',')[0]);
        toast.success(`${t('prayerTrial.found')}: ${result.display_name.split(',')[0]}`);
      } else {
        toast.error(t('prayerTrial.cityNotFound'));
      }
    } catch (error) {
      toast.error(t('prayerTrial.searchFailed'));
    }
    setLoadingLocation(false);
  };

  // Fetch calculated prayer times from Aladhan API
  const fetchCalculatedTimes = useCallback(async () => {
    if (!location) return;
    
    setLoadingCalculated(true);
    try {
      const today = new Date();
      const dateStr = `${today.getDate()}-${today.getMonth() + 1}-${today.getFullYear()}`;
      
      const response = await fetch(
        `https://api.aladhan.com/v1/timings/${dateStr}?latitude=${location.lat}&longitude=${location.lon}&method=${calculationMethod}`
      );
      const data = await response.json();
      
      if (data.code === 200 && data.data?.timings) {
        const timings = data.data.timings;
        setCalculatedTimes({
          fajr: { time: timings.Fajr },
          sunrise: { time: timings.Sunrise },
          dhuhr: { time: timings.Dhuhr },
          asr: { time: timings.Asr },
          maghrib: { time: timings.Maghrib },
          isha: { time: timings.Isha },
        });
        setLastUpdated(new Date().toISOString());
      }
    } catch (error) {
      console.error('Failed to fetch prayer times:', error);
      toast.error(t('prayerTrial.fetchFailed'));
    }
    setLoadingCalculated(false);
  }, [location, calculationMethod, t]);

  // Get user's language for API calls
  const getUserLanguage = useCallback(() => {
    const appLanguage = i18n.language || 'en';
    const languageMap = {
      'en': 'English', 'ar': 'Arabic', 'fr': 'French', 'es': 'Spanish',
      'de': 'German', 'tr': 'Turkish', 'ur': 'Urdu', 'id': 'Indonesian',
      'ms': 'Malay', 'zh': 'Chinese', 'pt': 'Portuguese', 'it': 'Italian',
      'nl': 'Dutch', 'ru': 'Russian', 'bn': 'Bengali', 'hi': 'Hindi',
    };
    return languageMap[appLanguage.split('-')[0]] || 'English';
  }, []);

  // =================================================================
  // MOSQUE FINDER ASSIST - Firecrawl + OSM Backend API
  // System A: Discovery/Localization (uses backend /api/mosque-discovery)
  // System B: Prayer extraction (unchanged - uses existing code)
  // =================================================================
  
  // Get unique user ID for usage tracking
  const getMosqueSearchUserId = () => {
    let userId = localStorage.getItem('tanzeen_mosque_user_id');
    if (!userId) {
      userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('tanzeen_mosque_user_id', userId);
    }
    return userId;
  };
  
  // Search mosques via backend API (postcode)
  const searchMosquesByPostcode = async (postcode) => {
    const userId = getMosqueSearchUserId();
    const response = await fetch(`${API}/mosque-discovery/search/postcode`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        postcode: postcode,
        user_id: userId
      })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Search failed');
    }
    
    return response.json();
  };
  
  // Search mosques via backend API (GPS)
  const searchMosquesByGPS = async (latitude, longitude) => {
    const userId = getMosqueSearchUserId();
    const response = await fetch(`${API}/mosque-discovery/search/gps`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        latitude: latitude,
        longitude: longitude,
        user_id: userId
      })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Search failed');
    }
    
    return response.json();
  };
  
  // UK Postcode regex - matches all valid UK formats with/without spaces
  // Examples: E1 1BB, E11BB, SW1A 1AA, SW1A1AA, SE8 3QW, EC1A1BB
  const UK_POSTCODE_REGEX = /^[A-Z]{1,2}[0-9][0-9A-Z]?[0-9][A-Z]{2}$/;
  
  // Detect if input is a website URL (must come AFTER postcode check in logic)
  const isWebsiteInput = (input) => {
    if (!input) return false;
    const trimmed = input.trim().toLowerCase();
    return trimmed.startsWith('http://') || 
           trimmed.startsWith('https://') ||
           trimmed.includes('.com') || 
           trimmed.includes('.org') ||
           trimmed.includes('.co.uk') ||
           trimmed.includes('.net') ||
           trimmed.startsWith('www.');
  };
  
  // Detect if input looks like a UK postcode - CHECK THIS FIRST before URL
  const isPostcodeInput = (input) => {
    if (!input) return false;
    // Normalize: remove all spaces, convert to uppercase
    const normalized = input.trim().toUpperCase().replace(/\s+/g, '');
    const isPostcode = UK_POSTCODE_REGEX.test(normalized);
    console.log(`[MosqueFinder] isPostcodeInput("${input}") -> normalized="${normalized}" -> ${isPostcode}`);
    return isPostcode;
  };
  
  // =================================================================
  // STRICT MOSQUE WEBSITE QUALIFICATION (KEPT - Used by System B)
  // Only accept URLs that look like actual mosque prayer sources
  // =================================================================
  const REJECTED_DOMAINS = [
    'loveforallhatredfornone',   // Ahmadiyya campaign site
    'facebook.com',              // Social media - not prayer source
    'twitter.com',
    'instagram.com',
    'youtube.com',
    'wikipedia.org',
    'justgiving.com',            // Charity fundraising
    'gofundme.com',
    'change.org',                // Petition sites
    'eventbrite.com',            // Event booking
  ];
  
  const REQUIRED_MOSQUE_INDICATORS = [
    'mosque', 'masjid', 'jamia', 'jami', 'markaz', 'islamic-centre', 
    'islamic-center', 'islamiccentre', 'islamiccenter', 'muslimcentre', 
    'muslimcenter', 'prayer', 'mawaqit', 'salah', 'jamaah', 'jamat', 'iqamah',
    'trust',  // Many UK mosques use "trust" in URL (e.g., salahuddintrust.co.uk)
  ];
  
  const validateMosqueWebsite = (website, mosqueName) => {
    if (!website) return { valid: false, reason: 'no_website' };
    
    const lowerUrl = website.toLowerCase();
    const lowerName = (mosqueName || '').toLowerCase();
    
    // Check for explicitly rejected domains
    for (const rejected of REJECTED_DOMAINS) {
      if (lowerUrl.includes(rejected)) {
        console.log(`[MosqueFinder] REJECTED: ${website} (blocked domain: ${rejected})`);
        return { valid: false, reason: `blocked_domain:${rejected}` };
      }
    }
    
    // Check for mosque indicators in URL or mosque name
    const hasIndicator = REQUIRED_MOSQUE_INDICATORS.some(indicator => 
      lowerUrl.includes(indicator) || lowerName.includes(indicator)
    );
    
    if (!hasIndicator) {
      console.log(`[MosqueFinder] LOW CONFIDENCE: ${website} (no mosque indicator in URL or name)`);
      // Still allow but flag as low confidence
      return { valid: true, confidence: 'low', reason: 'no_mosque_indicator' };
    }
    
    console.log(`[MosqueFinder] VALIDATED: ${website}`);
    return { valid: true, confidence: 'high', reason: 'has_mosque_indicator' };
  };
  
  // Handle GPS location search for nearby mosque - uses Firecrawl backend
  const handleGpsMosqueFinder = () => {
    console.log(`[MosqueFinder] GPS button clicked`);
    
    if (!navigator.geolocation) {
      console.error(`[MosqueFinder] Geolocation not supported`);
      toast.error(t('prayerTrial.gpsNotSupported') || 'GPS not supported in this browser');
      return;
    }
    
    setFindingNearbyMosque(true);
    toast.info(t('prayerTrial.detectingLocation') || 'Detecting your location...');
    
    console.log(`[MosqueFinder] Requesting geolocation...`);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        console.log(`[MosqueFinder] GPS SUCCESS - Lat: ${position.coords.latitude}, Lon: ${position.coords.longitude}`);
        
        try {
          const result = await searchMosquesByGPS(
            position.coords.latitude,
            position.coords.longitude
          );
          
          // Update remaining searches
          setMosqueSearchRemaining(result.remaining_searches);
          
          if (!result.success) {
            console.error(`[MosqueFinder] API error: ${result.error}`);
            toast.error(result.error || 'Search failed');
            return;
          }
          
          const selected = result.selected_mosque;
          
          if (selected?.website) {
            console.log(`[MosqueFinder] Auto-filling URL: ${selected.website}`);
            setMosqueUrlInput(selected.website);
            
            const msg = result.cached 
              ? `Found: ${selected.name} (cached)` 
              : `Found: ${selected.name}`;
            
            if (selected.confidence === 'high') {
              toast.success(msg);
            } else {
              toast.warning(`${msg} — Please verify this is the correct mosque.`);
            }
          } else if (result.mosques?.length > 0) {
            console.log(`[MosqueFinder] Found mosques but none with websites`);
            toast.info(`Found ${result.mosques.length} mosques nearby but no websites listed. Try searching for their website.`);
          } else {
            console.log(`[MosqueFinder] No mosques found`);
            toast.error('No nearby mosque found. Try entering a postcode.');
          }
        } catch (error) {
          console.error('[MosqueFinder] GPS search error:', error);
          toast.error(error.message || 'Could not find nearby mosques');
        } finally {
          setFindingNearbyMosque(false);
        }
      },
      (error) => {
        console.error(`[MosqueFinder] GPS ERROR - Code: ${error.code}, Message: ${error.message}`);
        setFindingNearbyMosque(false);
        
        let errorMessage = 'Could not get your location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied. Please enable location access in your browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location unavailable. Try entering a postcode instead.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out. Please try again.';
            break;
          default:
            break;
        }
        toast.error(errorMessage);
      },
      { 
        timeout: 15000, 
        enableHighAccuracy: false,
        maximumAge: 60000  // Accept cached location up to 1 minute old
      }
    );
  };
  
  // Handle postcode search for nearby mosque - uses Firecrawl backend
  const handlePostcodeMosqueFinder = async (postcode) => {
    console.log(`[MosqueFinder] Postcode search started: "${postcode}"`);
    setFindingNearbyMosque(true);
    toast.info(`Searching near ${postcode}...`);
    
    try {
      const result = await searchMosquesByPostcode(postcode);
      
      // Update remaining searches
      setMosqueSearchRemaining(result.remaining_searches);
      
      if (!result.success) {
        console.error(`[MosqueFinder] API error: ${result.error}`);
        toast.error(result.error || 'Search failed');
        return;
      }
      
      const selected = result.selected_mosque;
      
      if (selected?.website) {
        const distanceKm = selected.distance_km ? `${selected.distance_km.toFixed(1)}km` : '';
        const cacheNote = result.cached ? ' (cached)' : '';
        
        if (selected.confidence === 'high') {
          console.log(`[MosqueFinder] ✓ Found HIGH CONFIDENCE mosque: ${selected.website}`);
          setMosqueUrlInput(selected.website);
          toast.success(`Found: ${selected.name}${distanceKm ? ` (${distanceKm})` : ''}${cacheNote}`);
        } else {
          console.log(`[MosqueFinder] ⚠ Found MEDIUM/LOW CONFIDENCE mosque: ${selected.website}`);
          setMosqueUrlInput(selected.website);
          toast.warning(`Found: ${selected.name}${distanceKm ? ` (${distanceKm})` : ''}${cacheNote} — Please verify this is the correct mosque.`);
        }
      } else if (result.mosques?.length > 0) {
        console.log(`[MosqueFinder] Found ${result.mosques.length} mosques but none selected`);
        toast.info(`Found ${result.mosques.length} mosques nearby but no websites listed. Try searching online for their prayer times.`);
      } else {
        console.log(`[MosqueFinder] No verified mosque prayer source found near postcode`);
        toast.error('No verified mosque prayer source found nearby. Try entering a mosque website URL directly.');
      }
    } catch (error) {
      console.error('[MosqueFinder] Postcode search error:', error);
      toast.error(error.message || 'Search failed. Please try again.');
    } finally {
      setFindingNearbyMosque(false);
    }
  };
  
  // Handle URL input change with postcode detection
  const handleMosqueUrlInputChange = (value) => {
    setMosqueUrlInput(value);
  };
  
  // Handle Enter key or button click - detect postcode vs URL
  // CRITICAL: Check postcode FIRST, then URL
  const handleMosqueInputSubmit = () => {
    const rawInput = mosqueUrlInput.trim();
    const normalizedInput = rawInput.toUpperCase().replace(/\s+/g, ' ').trim();
    
    console.log(`[MosqueFinder] ========================================`);
    console.log(`[MosqueFinder] Submit triggered`);
    console.log(`[MosqueFinder] Raw input: "${rawInput}"`);
    console.log(`[MosqueFinder] Normalized: "${normalizedInput}"`);
    
    // STEP 1: Check if it's a UK postcode FIRST (before URL check)
    if (isPostcodeInput(rawInput)) {
      console.log(`[MosqueFinder] ✓ Detected as UK POSTCODE - running mosque finder`);
      handlePostcodeMosqueFinder(rawInput);
      return; // IMPORTANT: Stop here, do NOT run extraction
    }
    
    // STEP 2: Check if it's a website URL
    if (isWebsiteInput(rawInput)) {
      console.log(`[MosqueFinder] ✓ Detected as WEBSITE URL - running extraction`);
      extractMosqueTimes();
      return;
    }
    
    // STEP 3: Fallback - treat as potential URL and try extraction
    console.log(`[MosqueFinder] ✗ Not recognized as postcode or URL - trying extraction anyway`);
    extractMosqueTimes();
  };

  // Extract mosque times (PRO feature) with caching
  const extractMosqueTimes = async (forceRefresh = false) => {
    if (!mosqueUrlInput.trim()) {
      toast.error(t('prayerTrial.enterMosqueUrl'));
      return;
    }

    // 🏰 FORTRESS: Check if feature is globally enabled
    if (!isMosqueSyncEnabled && fortressLoaded) {
      toast.error(t('prayerTrial.featureTemporarilyUnavailable', 'This feature is temporarily unavailable. Please try again later.'));
      return;
    }
    
    // 🏰 FORTRESS: Validate access with backend (single source of truth)
    if (fortressLoaded) {
      const accessResult = await validateAccess('mosque_sync');
      if (!accessResult.allowed) {
        console.log('[Fortress] Mosque Sync access denied:', accessResult.reason);
        
        // Handle based on reason
        if (accessResult.requirePayment || !accessResult.canUseAd) {
          requestFeatureAccess(PRO_FEATURES.MOSQUE_FINDER);
          return;
        }
        
        toast.error(accessResult.reason || "Access denied. Please upgrade or try again.");
        return;
      }
      
      // Log Fortress validation success
      console.log('[Fortress] Mosque Sync access granted');
    }

    // STRICT USAGE GATING for Ad-Unlocked Users (1 extraction per ad)
    if (!isPro) {
      // Check if user has remaining extractions via ad unlock
      // Check all possible feature IDs that might be unlocked
      const statusMosqueFinder = getFeatureUnlockStatus('mosque_finder');
      const statusSmartConnect = getFeatureUnlockStatus('smart_mosque_connect');
      const statusPrayerExtraction = getFeatureUnlockStatus('prayer_extraction');
      
      // Use whichever one is unlocked
      const featureStatus = statusMosqueFinder.unlocked ? statusMosqueFinder : 
                           (statusSmartConnect.unlocked ? statusSmartConnect : statusPrayerExtraction);
      
      if (!featureStatus.unlocked || (featureStatus.usesRemaining !== undefined && featureStatus.usesRemaining <= 0)) {
        // No uses remaining - show upgrade modal
        requestFeatureAccess(PRO_FEATURES.MOSQUE_FINDER);
        return;
      }
      
      // Consume one extraction use from whichever feature is unlocked
      const consumed = consumeFeatureUseAction('mosque_finder') || 
                      consumeFeatureUseAction('smart_mosque_connect') || 
                      consumeFeatureUseAction('prayer_extraction');
      if (!consumed) {
        // Failed to consume - show upgrade modal  
        requestFeatureAccess(PRO_FEATURES.MOSQUE_FINDER);
        return;
      }
      
      // Notify user this was their unlocked extraction
      toast.info(t('prayerTrial.adUnlockedExtraction', 'Ad-unlocked extraction used. Watch another ad for more.'), { duration: 3000 });
    } else {
      // PRO-GATE: Check if user can use this AI feature (server-side verified) for Pro users
      const usageCheck = await checkProGate('mosque');
      if (!usageCheck.allowed) {
        // Show Pro upgrade modal
        promptUpgrade(PRO_FEATURES.MOSQUE_FINDER);
        return;
      }
    }

    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cachedData = loadCachedMosqueData(mosqueUrlInput.trim());
      if (cachedData) {
        setMosqueResult(cachedData);
        setUsingCachedData(true);
        toast.success(t('prayerTrial.loadedFromCache'));
        return;
      }
    }

    setMosqueExtracting(true);
    setMosqueResult(null);
    setUsingCachedData(false);

    // HARD CLIENT-SIDE TIMEOUT for the whole async flow (POST + polling).
    // Backend p95 is ~40s; we give ourselves 120s of headroom before giving up.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000);

    try {
      const appLanguage = i18n.language || 'en';
      const languageMap = {
        'en': 'English', 'ar': 'Arabic', 'fr': 'French', 'es': 'Spanish',
        'de': 'German', 'tr': 'Turkish', 'ur': 'Urdu', 'id': 'Indonesian',
        'ms': 'Malay', 'zh': 'Chinese', 'pt': 'Portuguese', 'it': 'Italian',
        'nl': 'Dutch', 'ru': 'Russian', 'bn': 'Bengali', 'hi': 'Hindi',
      };
      const userLanguage = languageMap[appLanguage.split('-')[0]] || 'English';

      // Async job pattern: POST returns fast with {job_id}, we poll until done.
      const data = await extractV2Async({
        url: mosqueUrlInput.trim(),
        language: userLanguage,
        user_location: location,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      
      // V2 API returns data directly (not wrapped in extraction_result)
      if (data.success && data.prayers) {
        const result = {
          success: true,
          mosqueName: data.mosque_name || formatUrlToMosqueName(mosqueUrlInput),
          address: data.address,
          prayers: {
            fajr: { 
              start: data.prayers?.fajr?.athan || null, 
              jamaah: data.prayers?.fajr?.iqamah || data.prayers?.fajr?.iqama || null 
            },
            sunrise: { time: data.prayers?.sunrise?.time || null },
            dhuhr: { 
              start: data.prayers?.dhuhr?.athan || null, 
              jamaah: data.prayers?.dhuhr?.iqamah || data.prayers?.dhuhr?.iqama || null 
            },
            asr: { 
              start: data.prayers?.asr?.athan || null, 
              jamaah: data.prayers?.asr?.iqamah || data.prayers?.asr?.iqama || null 
            },
            maghrib: { 
              start: data.prayers?.maghrib?.athan || null, 
              jamaah: data.prayers?.maghrib?.iqamah || data.prayers?.maghrib?.iqama || null 
            },
            isha: { 
              start: data.prayers?.isha?.athan || null, 
              jamaah: data.prayers?.isha?.iqamah || data.prayers?.isha?.iqama || null 
            },
            jumuah: data.prayers?.jumuah?.salah || data.prayers?.jumuah?.khutbah || null,
          },
          logistics: data.logistics || null,
          dataSource: data.data_source || 'v2_extraction',
          extractedCount: data.extracted_count || 6,
          confidence: data.confidence || 0,
          extractedDate: data.extracted_date || null,  // Calendar date from timetable
          stale: !!data.stale,                  // true => yesterday's fallback served
          cached: !!data.cached,                // true => Mongo cache hit (no Gemini call)
          scheduleDate: data.schedule_date || null,
          lastUpdated: new Date().toISOString(),
        };
        
        setMosqueResult(result);
        setLastUpdated(new Date().toISOString());
        setMode('mosque');  // Switch to mosque mode to display Jama'ah times correctly
        
        // PRO-GATE: Record successful usage (server-side)
        await recordUsage('mosque');
        
        // 🏰 FORTRESS: Consume tokens after successful AI usage
        if (fortressLoaded) {
          try {
            await fortressConsumeTokens('mosque_sync');
            console.log('[Fortress] Tokens consumed for mosque_sync');
          } catch (e) {
            console.warn('[Fortress] Token consumption failed:', e);
          }
        }
        
        // Save to cache
        saveMosqueDataToCache(mosqueUrlInput.trim(), result);
        
        // Show different message for Pro vs Ad-unlocked users
        if (isPro) {
          toast.success(`${t('prayerTrial.found')} ${data.extracted_count} ${t('prayerTrial.prayerTimes')}!`);
        } else {
          // Ad-rewarded user - show success + upgrade prompt
          toast.success(
            t('prayerTrial.adExtractionSuccess', 'Extraction successful! Upgrade to Pro for automatic daily updates.'),
            { duration: 5000 }
          );
        }
      } else {
        setMosqueResult({
          success: false,
          error: data.error || t('prayerTrial.extractionFailed'),
        });
        toast.error(data.error || t('prayerTrial.extractionFailed'));
      }
    } catch (error) {
      console.error('Extraction error:', error);
      let errorMessage = t('prayerTrial.networkError');
      if (error?.name === 'AbortError') {
        errorMessage = t('prayerTrial.extractionTimeout', 'Extraction took too long. Please try again.');
      } else if (error?.message === 'Extraction timed out') {
        errorMessage = t('prayerTrial.extractionTimeout', 'Extraction took too long. Please try again.');
      } else if (error?.message && error.message !== 'Failed to fetch') {
        // Surface the actual server-side error (e.g. "Extraction failed: ...")
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      setMosqueResult({
        success: false,
        error: errorMessage,
      });
    }
    
    setMosqueExtracting(false);
  };

  // TRIAL: Search Mosque by GPS - Uses V2 Engine with Mawaqit Fallback
  const searchMosqueByGPS = async () => {
    // PRO-GATE: Check if user can use this AI feature
    const usageCheck = await checkProGate('mosque');
    if (!usageCheck.allowed) {
      promptUpgrade(PRO_FEATURES.MOSQUE_FINDER);
      return;
    }

    setFindingNearbyMosque(true);
    setMosqueResult(null);

    // Get GPS location
    let userLat = location?.lat;
    let userLon = location?.lon;

    if (!userLat || !userLon) {
      try {
        toast.info(t('prayerTrial.gettingLocation') || 'Getting your location...');
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { 
            timeout: 30000,           // 30 seconds timeout
            enableHighAccuracy: false, // Use Wi-Fi/Cell towers as fallback
            maximumAge: 60000          // Accept 1-minute old cached location
          });
        });
        userLat = position.coords.latitude;
        userLon = position.coords.longitude;
      } catch (geoErr) {
        toast.error(t('prayerTrial.locationError') || 'Could not get location');
        setFindingNearbyMosque(false);
        return;
      }
    }

    toast.info(t('prayerTrial.searchingNearby') || 'Finding nearest mosque...');

    try {
      // 60-second timeout for V2 Find Nearby (Gemini + Playwright can take 30-45s)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);
      
      // Call V2 Find Nearby - Uses Gemini + V2 Engine with Mawaqit fallback
      const response = await fetch(`${API}/prayer-trial-v2/find-nearby`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lat: userLat,
          lon: userLon,
          language: getUserLanguage(),
        }),
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      const data = await response.json();

      if (data.success && data.prayers) {
        const result = {
          success: true,
          mosqueName: data.mosque_name || t('prayerTrial.nearestMosque'),
          address: data.address || '',
          prayers: {
            fajr: { start: data.prayers.fajr?.athan, jamaah: data.prayers.fajr?.iqamah || data.prayers.fajr?.iqama },
            sunrise: { time: data.prayers.sunrise?.time },
            dhuhr: { start: data.prayers.dhuhr?.athan, jamaah: data.prayers.dhuhr?.iqamah || data.prayers.dhuhr?.iqama },
            asr: { start: data.prayers.asr?.athan, jamaah: data.prayers.asr?.iqamah || data.prayers.asr?.iqama },
            maghrib: { start: data.prayers.maghrib?.athan, jamaah: data.prayers.maghrib?.iqamah || data.prayers.maghrib?.iqama },
            isha: { start: data.prayers.isha?.athan, jamaah: data.prayers.isha?.iqamah || data.prayers.isha?.iqama },
            jumuah: data.prayers.jumuah?.salah || data.prayers.jumuah?.khutbah || null,
          },
          logistics: data.logistics || null,
          dataSource: data.data_source || 'v2_gps_search',
          extractedCount: data.extracted_count || 6,
          confidence: data.confidence || 0,
        };

        setMosqueResult(result);
        setMode('mosque');
        
        // Record usage
        await recordUsage('mosque');
        
        // Show different message for Pro vs Ad-unlocked users
        if (isPro) {
          toast.success(`${t('prayerTrial.found')}: ${result.mosqueName}`);
        } else {
          toast.success(
            t('prayerTrial.adExtractionSuccess', 'Extraction successful! Upgrade to Pro for automatic daily updates.'),
            { duration: 5000 }
          );
        }
      } else {
        toast.error(data.error || t('prayerTrial.noNearbyMosques'));
        setMosqueResult({ success: false, error: data.error });
      }
    } catch (error) {
      console.error('Search mosque error:', error);
      toast.error(t('prayerTrial.networkError'));
    }

    setFindingNearbyMosque(false);
  };

  // STATE: IP-based search loading
  const [searchingByIP, setSearchingByIP] = useState(false);

  // METHOD 2: Search Mosque by IP Detection (Fast Fallback)
  const searchMosqueByIP = async () => {
    // PRO-GATE: Check if user can use this AI feature
    const usageCheck = await checkProGate('mosque');
    if (!usageCheck.allowed) {
      promptUpgrade(PRO_FEATURES.MOSQUE_FINDER);
      return;
    }

    setSearchingByIP(true);
    setMosqueResult(null);

    toast.info(t('prayerTrial.detectingIP') || 'Detecting city via IP...');

    try {
      // Step 1: Get location from IP (5-second timeout)
      const ipController = new AbortController();
      const ipTimeoutId = setTimeout(() => ipController.abort(), 5000);
      
      let ipData;
      try {
        // Using ip-api.com (free, no API key needed, 45 req/min limit)
        const ipResponse = await fetch('http://ip-api.com/json/?fields=status,city,regionName,country,lat,lon,zip', {
          signal: ipController.signal,
        });
        clearTimeout(ipTimeoutId);
        ipData = await ipResponse.json();
      } catch (ipError) {
        clearTimeout(ipTimeoutId);
        if (ipError.name === 'AbortError') {
          toast.error(t('prayerTrial.ipDetectionFailed') || 'Detection failed. Please enter your Postcode manually.');
        } else {
          toast.error(t('prayerTrial.ipDetectionFailed') || 'IP detection failed. Please try GPS or enter postcode.');
        }
        setSearchingByIP(false);
        return;
      }

      if (ipData.status !== 'success' || !ipData.lat || !ipData.lon) {
        toast.error(t('prayerTrial.ipDetectionFailed') || 'Could not detect location. Please enter your Postcode manually.');
        setSearchingByIP(false);
        return;
      }

      const detectedCity = ipData.city || ipData.regionName;
      toast.info(`${t('prayerTrial.searchingNearby') || 'Searching nearby Masjids in'} ${detectedCity}...`);

      // Step 2: Call V2 Find Nearby with IP-detected coordinates
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);
      
      const response = await fetch(`${API}/prayer-trial-v2/find-nearby`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lat: ipData.lat,
          lon: ipData.lon,
          language: getUserLanguage(),
        }),
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      const data = await response.json();

      if (data.success && data.prayers) {
        const result = {
          success: true,
          mosqueName: data.mosque_name || t('prayerTrial.nearestMosque'),
          address: data.address || `${detectedCity}, ${ipData.country}`,
          prayers: {
            fajr: { start: data.prayers.fajr?.athan, jamaah: data.prayers.fajr?.iqamah || data.prayers.fajr?.iqama },
            sunrise: { time: data.prayers.sunrise?.time },
            dhuhr: { start: data.prayers.dhuhr?.athan, jamaah: data.prayers.dhuhr?.iqamah || data.prayers.dhuhr?.iqama },
            asr: { start: data.prayers.asr?.athan, jamaah: data.prayers.asr?.iqamah || data.prayers.asr?.iqama },
            maghrib: { start: data.prayers.maghrib?.athan, jamaah: data.prayers.maghrib?.iqamah || data.prayers.maghrib?.iqama },
            isha: { start: data.prayers.isha?.athan, jamaah: data.prayers.isha?.iqamah || data.prayers.isha?.iqama },
            jumuah: data.prayers.jumuah?.salah || data.prayers.jumuah?.khutbah || null,
          },
          logistics: data.logistics || null,
          dataSource: data.data_source || 'v2_ip_search',
          extractedCount: data.extracted_count || 6,
          confidence: data.confidence || 0,
        };

        setMosqueResult(result);
        setMode('mosque');
        
        // Record usage (only on success)
        await recordUsage('mosque');
        
        // Show different message for Pro vs Ad-unlocked users
        if (isPro) {
          toast.success(`${t('prayerTrial.found')}: ${result.mosqueName}`);
        } else {
          toast.success(
            t('prayerTrial.adExtractionSuccess', 'Extraction successful! Upgrade to Pro for automatic daily updates.'),
            { duration: 5000 }
          );
        }
      } else {
        toast.error(data.error || t('prayerTrial.noNearbyMosques'));
        setMosqueResult({ success: false, error: data.error });
      }
    } catch (error) {
      console.error('IP search error:', error);
      if (error.name === 'AbortError') {
        toast.error(t('prayerTrial.searchTimeout') || 'Search timed out. Please try again.');
      } else {
        toast.error(t('prayerTrial.networkError'));
      }
    }

    setSearchingByIP(false);
  };

  // ============================================================================
  // MANUAL SEARCH - Search by Postcode or Mosque Name (V2 Gemini)
  // ============================================================================
  const handleManualSearch = async () => {
    const query = manualSearchQuery.trim();
    if (!query) {
      toast.error(t('prayerTrial.enterSearchQuery') || 'Please enter a mosque name or postcode');
      return;
    }

    // PRO-GATE: Check if user can use this AI feature
    const usageCheck = await checkProGate('mosque');
    if (!usageCheck.allowed) {
      promptUpgrade(PRO_FEATURES.MOSQUE_FINDER);
      return;
    }

    setIsManualSearching(true);
    setMosqueResult(null);

    toast.info(t('prayerTrial.searchingByQuery') || `Searching for "${query}"...`);

    // Detect user's country from browser locale or default to UK
    const getUserCountry = () => {
      const locale = navigator.language || 'en-GB';
      const countryCode = locale.split('-')[1] || 'GB';
      const countryMap = {
        'GB': 'United Kingdom', 'UK': 'United Kingdom',
        'US': 'United States', 'CA': 'Canada', 'AU': 'Australia',
        'FR': 'France', 'DE': 'Germany', 'NL': 'Netherlands',
        'BE': 'Belgium', 'PK': 'Pakistan', 'IN': 'India',
        'AE': 'United Arab Emirates', 'SA': 'Saudi Arabia',
        'MY': 'Malaysia', 'ID': 'Indonesia', 'TR': 'Turkey',
        'EG': 'Egypt', 'NG': 'Nigeria', 'ZA': 'South Africa',
      };
      return countryMap[countryCode.toUpperCase()] || 'United Kingdom';
    };

    try {
      // Use Gemini to find mosque by name/postcode
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);

      // Call backend to search by query with country context
      const response = await fetch(`${API}/prayer-trial-v2/search-by-query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: query,
          language: getUserLanguage(),
          country: getUserCountry(),
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      const data = await response.json();

      if (data.success && data.prayers) {
        const result = {
          success: true,
          mosqueName: data.mosque_name || query,
          address: data.address || '',
          prayers: {
            fajr: { start: data.prayers.fajr?.athan, jamaah: data.prayers.fajr?.iqamah || data.prayers.fajr?.iqama },
            sunrise: { time: data.prayers.sunrise?.time },
            dhuhr: { start: data.prayers.dhuhr?.athan, jamaah: data.prayers.dhuhr?.iqamah || data.prayers.dhuhr?.iqama },
            asr: { start: data.prayers.asr?.athan, jamaah: data.prayers.asr?.iqamah || data.prayers.asr?.iqama },
            maghrib: { start: data.prayers.maghrib?.athan, jamaah: data.prayers.maghrib?.iqamah || data.prayers.maghrib?.iqama },
            isha: { start: data.prayers.isha?.athan, jamaah: data.prayers.isha?.iqamah || data.prayers.isha?.iqama },
            jumuah: data.prayers.jumuah?.salah || data.prayers.jumuah?.khutbah || null,
          },
          logistics: data.logistics || null,
          dataSource: data.data_source || 'v2_manual_search',
          extractedCount: data.extracted_count || 6,
          confidence: data.confidence || 0,
          searchedUrl: data.url || null,
        };

        setMosqueResult(result);
        setMode('mosque');

        // Record usage (only on success)
        await recordUsage('mosque');

        toast.success(`${t('prayerTrial.found')}: ${result.mosqueName}`);
      } else {
        toast.error(data.error || t('prayerTrial.noMosquesFound') || 'No mosques found for this search');
        setMosqueResult({ success: false, error: data.error });
      }
    } catch (error) {
      console.error('Manual search error:', error);
      if (error.name === 'AbortError') {
        toast.error(t('prayerTrial.searchTimeout') || 'Search timed out. Please try again.');
      } else {
        toast.error(t('prayerTrial.networkError'));
      }
    }

    setIsManualSearching(false);
  };

  // ============================================================================
  // MY MASJIDS - PRO Feature (MongoDB backed)
  // ============================================================================
  
  // Get user ID for MongoDB
  const getUserId = () => {
    let userId = localStorage.getItem('quranplus_user_id');
    if (!userId) {
      userId = 'user_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
      localStorage.setItem('quranplus_user_id', userId);
    }
    return userId;
  };

  // Load saved masjids on mount
  useEffect(() => {
    const loadSavedMasjids = async () => {
      if (!isPro) return;
      
      setLoadingSavedMasjids(true);
      try {
        const response = await fetch(`${API}/masjids/list/${getUserId()}`);
        const data = await response.json();
        
        if (data.success && data.masjids) {
          setSavedMasjids(data.masjids);
        }
      } catch (error) {
        console.error('Error loading saved masjids:', error);
      }
      setLoadingSavedMasjids(false);
    };

    loadSavedMasjids();
  }, [isPro]);

  // Save current masjid to favorites
  const saveMasjidToFavorites = async () => {
    if (!isPro) {
      promptUpgrade(PRO_FEATURES.MOSQUE_FINDER);
      return;
    }

    if (!mosqueResult?.success || !mosqueResult?.mosqueName) {
      toast.error('No masjid to save');
      return;
    }

    setSavingMasjid(true);
    try {
      const response = await fetch(`${API}/masjids/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: getUserId(),
          masjid_name: mosqueResult.mosqueName,
          masjid_url: mosqueResult.searchedUrl || mosqueUrlInput || manualSearchQuery,
          address: mosqueResult.address,
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success(data.message || 'Masjid saved!');
        // Reload saved masjids
        const listResponse = await fetch(`${API}/masjids/list/${getUserId()}`);
        const listData = await listResponse.json();
        if (listData.success) {
          setSavedMasjids(listData.masjids);
        }
      } else {
        toast.error(data.error || 'Failed to save masjid');
      }
    } catch (error) {
      console.error('Save masjid error:', error);
      toast.error('Failed to save masjid');
    }
    setSavingMasjid(false);
  };

  // Remove masjid from favorites
  const removeMasjidFromFavorites = async (masjidUrl) => {
    try {
      const response = await fetch(`${API}/masjids/remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: getUserId(),
          masjid_url: masjidUrl,
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Masjid removed');
        setSavedMasjids(prev => prev.filter(m => m.masjid_url !== masjidUrl));
      } else {
        toast.error(data.error || 'Failed to remove masjid');
      }
    } catch (error) {
      console.error('Remove masjid error:', error);
      toast.error('Failed to remove masjid');
    }
  };

  // Quick-tap to load saved masjid
  const loadSavedMasjid = async (masjid) => {
    // PRO-GATE: Still check usage even for saved masjids
    const usageCheck = await checkProGate('mosque');
    if (!usageCheck.allowed) {
      promptUpgrade(PRO_FEATURES.MOSQUE_FINDER);
      return;
    }

    setMosqueUrlInput(masjid.masjid_url);
    setMosqueExtracting(true);
    setMosqueResult(null);

    toast.info(`Loading ${masjid.masjid_name}...`);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000);

      const data = await extractV2Async({
        url: masjid.masjid_url,
        language: getUserLanguage(),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (data.success && data.prayers) {
        const result = {
          success: true,
          mosqueName: data.mosque_name || masjid.masjid_name,
          address: data.address || masjid.address,
          prayers: {
            fajr: { start: data.prayers.fajr?.athan, jamaah: data.prayers.fajr?.iqamah || data.prayers.fajr?.iqama },
            sunrise: { time: data.prayers.sunrise?.time },
            dhuhr: { start: data.prayers.dhuhr?.athan, jamaah: data.prayers.dhuhr?.iqamah || data.prayers.dhuhr?.iqama },
            asr: { start: data.prayers.asr?.athan, jamaah: data.prayers.asr?.iqamah || data.prayers.asr?.iqama },
            maghrib: { start: data.prayers.maghrib?.athan, jamaah: data.prayers.maghrib?.iqamah || data.prayers.maghrib?.iqama },
            isha: { start: data.prayers.isha?.athan, jamaah: data.prayers.isha?.iqamah || data.prayers.isha?.iqama },
            jumuah: data.prayers.jumuah?.salah || data.prayers.jumuah?.khutbah || null,
          },
          logistics: data.logistics || null,
          dataSource: 'saved_masjid',
          extractedCount: data.extracted_count || 6,
          confidence: data.confidence || 0,
          searchedUrl: masjid.masjid_url,
        };

        setMosqueResult(result);
        setMode('mosque');

        // Record usage (every extraction is logged for analytics)
        await recordUsage('mosque');

        toast.success(`${t('prayerTrial.found')}: ${result.mosqueName}`);
      } else {
        toast.error(data.error || 'Failed to load masjid times');
      }
    } catch (error) {
      console.error('Load saved masjid error:', error);
      toast.error('Failed to load masjid times');
    }

    setMosqueExtracting(false);
  };

  // NEW: Direct V2 Find Mosque - Uses already-detected location
  const findMosqueV2 = async () => {
    // Check PRO status - if not PRO, show upgrade prompt
    if (!isPro) {
      toast.error(t('prayerTrial.proFeatureRequired'));
      // TODO: Open PRO upgrade modal here
      return;
    }

    setFindingNearbyMosque(true);
    setMosqueResult(null);

    // Use already-detected location from calculated times
    let userLat = location?.lat;
    let userLon = location?.lon;

    // If no location in state, try one more time
    if (!userLat || !userLon) {
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { 
            timeout: 30000,           // 30 seconds timeout
            enableHighAccuracy: false, // Use Wi-Fi/Cell towers as fallback  
            maximumAge: 60000          // Accept 1-minute old cached location
          });
        });
        userLat = position.coords.latitude;
        userLon = position.coords.longitude;
      } catch (geoErr) {
        // Final fallback: use city search input
        if (citySearch && citySearch.trim().length > 2) {
          try {
            toast.info('Getting coordinates for ' + citySearch + '...');
            const geoResponse = await fetch(
              `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(citySearch.trim())}&format=json&limit=1`
            );
            const geoData = await geoResponse.json();
            if (geoData && geoData.length > 0) {
              userLat = parseFloat(geoData[0].lat);
              userLon = parseFloat(geoData[0].lon);
            }
          } catch (e) {
            console.error('Geocode error:', e);
          }
        }
      }
    }

    if (!userLat || !userLon) {
      toast.error('Location not available. Please enter a city name in the search box above.');
      setFindingNearbyMosque(false);
      return;
    }

    // Call V2 Find Nearby API
    toast.info('Finding nearest mosque...');
    
    try {
      const response = await fetch(`${API}/prayer-trial-v2/find-nearby`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lat: userLat,
          lon: userLon,
          language: getUserLanguage(),
        }),
      });
      
      const data = await response.json();

      if (data.success && data.prayers) {
        const result = {
          success: true,
          mosqueName: data.mosque_name || 'Nearest Mosque',
          address: data.address || '',
          prayers: {
            fajr: { start: data.prayers.fajr?.athan, jamaah: data.prayers.fajr?.iqamah || data.prayers.fajr?.iqama },
            sunrise: { time: data.prayers.sunrise?.time },
            dhuhr: { start: data.prayers.dhuhr?.athan, jamaah: data.prayers.dhuhr?.iqamah || data.prayers.dhuhr?.iqama },
            asr: { start: data.prayers.asr?.athan, jamaah: data.prayers.asr?.iqamah || data.prayers.asr?.iqama },
            maghrib: { start: data.prayers.maghrib?.athan, jamaah: data.prayers.maghrib?.iqamah || data.prayers.maghrib?.iqama },
            isha: { start: data.prayers.isha?.athan, jamaah: data.prayers.isha?.iqamah || data.prayers.isha?.iqama },
            jumuah: data.prayers.jumuah?.salah || data.prayers.jumuah?.khutbah || null,
          },
          logistics: data.logistics || null,
          dataSource: data.data_source || 'v2_extraction',
          extractedCount: data.extracted_count || 6,
          confidence: data.confidence || 0,
        };

        setMosqueResult(result);
        setMode('mosque');
        toast.success(`Found: ${result.mosqueName}`);
      } else {
        toast.error(data.error || 'Could not find nearby mosque');
        setMosqueResult({ success: false, error: data.error });
      }
    } catch (error) {
      console.error('Find mosque error:', error);
      toast.error('Network error. Please try again.');
    }

    setFindingNearbyMosque(false);
  };

  // Find nearest mosque using GPS → Gemini → V2 WIXPROOF extraction
  // Falls back to city search if GPS fails
  const findNearbyMosque = async () => {
    if (!isPro) {
      toast.error(t('prayerTrial.proFeatureRequired'));
      return;
    }

    setFindingNearbyMosque(true);
    setMosqueResult(null);

    try {
      // Get current location
      let userLat, userLon;
      
      if (location) {
        userLat = location.lat;
        userLon = location.lon;
      } else {
        // Try to get location from GPS
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { 
              timeout: 30000,           // 30 seconds timeout
              enableHighAccuracy: false, // Use Wi-Fi/Cell towers as fallback
              maximumAge: 60000          // Accept 1-minute old cached location
            });
          });
          userLat = position.coords.latitude;
          userLon = position.coords.longitude;
        } catch (geoError) {
          // GPS failed - try to use the city search input as fallback
          if (citySearch && citySearch.trim()) {
            // Use city name to get coordinates via geocoding
            try {
              const geoResponse = await fetch(
                `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(citySearch.trim())}&format=json&limit=1`
              );
              const geoData = await geoResponse.json();
              
              if (geoData && geoData.length > 0) {
                userLat = parseFloat(geoData[0].lat);
                userLon = parseFloat(geoData[0].lon);
                toast.info(`Using location: ${geoData[0].display_name?.split(',')[0] || citySearch}`);
              } else {
                toast.error(t('prayerTrial.locationError') + ' ' + (t('prayerTrial.enterCityFirst') || 'Please enter a city name first.'));
                setFindingNearbyMosque(false);
                return;
              }
            } catch (geocodeError) {
              toast.error(t('prayerTrial.locationError'));
              setFindingNearbyMosque(false);
              return;
            }
          } else {
            toast.error(t('prayerTrial.locationError') + ' ' + (t('prayerTrial.enterCityFirst') || 'Please enter a city name first.'));
            setFindingNearbyMosque(false);
            return;
          }
        }
      }

      // NEW FLOW: GPS → Gemini Intent → V2 WIXPROOF Extraction
      toast.info(t('prayerTrial.searchingNearby') || 'Finding nearest mosque...');
      
      const response = await fetch(`${API}/prayer-trial-v2/find-nearby`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lat: userLat,
          lon: userLon,
          language: getUserLanguage(),
        }),
      });
      
      const data = await response.json();

      if (data.success && data.prayers) {
        // Map V2 response format to our UI format
        const result = {
          success: true,
          mosqueName: data.mosque_name || t('prayerTrial.nearestMosque'),
          address: data.address || '',
          prayers: {
            fajr: { 
              start: data.prayers.fajr?.athan || null, 
              jamaah: data.prayers.fajr?.iqamah || data.prayers.fajr?.iqama || null 
            },
            sunrise: { time: data.prayers.sunrise?.time || null },
            dhuhr: { 
              start: data.prayers.dhuhr?.athan || null,
              jamaah: data.prayers.dhuhr?.iqamah || data.prayers.dhuhr?.iqama || null
            },
            asr: { 
              start: data.prayers.asr?.athan || null,
              jamaah: data.prayers.asr?.iqamah || data.prayers.asr?.iqama || null
            },
            maghrib: { 
              start: data.prayers.maghrib?.athan || null,
              jamaah: data.prayers.maghrib?.iqamah || data.prayers.maghrib?.iqama || null
            },
            isha: { 
              start: data.prayers.isha?.athan || null,
              jamaah: data.prayers.isha?.iqamah || data.prayers.isha?.iqama || null
            },
            jumuah: data.prayers.jumuah?.salah || data.prayers.jumuah?.khutbah || null,
          },
          logistics: data.logistics || null,
          dataSource: data.data_source || 'v2_extraction',
          extractedCount: data.extracted_count || 6,
          confidence: data.confidence || 0,
        };

        setMosqueResult(result);
        setMode('mosque');
        
        // Save to cache
        saveMosqueDataToCache(`nearby_${userLat}_${userLon}`, result);
        
        toast.success(`${t('prayerTrial.found')}: ${result.mosqueName}`);
      } else {
        toast.error(data.error || t('prayerTrial.noNearbyMosques'));
        setMosqueResult({
          success: false,
          error: data.error || t('prayerTrial.noNearbyMosques'),
        });
      }
    } catch (error) {
      console.error('Find nearby mosque error:', error);
      toast.error(t('prayerTrial.networkError'));
      setMosqueResult({
        success: false,
        error: t('prayerTrial.networkError'),
      });
    }

    setFindingNearbyMosque(false);
  };

  // Handle PRO mode toggle
  const handleModeToggle = () => {
    if (mode === 'calculated') {
      if (!hasMosqueAccess) {
        requestFeatureAccess(PRO_FEATURES.MOSQUE_FINDER, () => {
          setMode('mosque');
        });
        return;
      }
      setMode('mosque');
    } else {
      setMode('calculated');
      // Clear saved mosque data when switching back to calculated
      clearSavedMosqueData();
    }
  };

  const selectedMethod = CALCULATION_METHODS.find(m => m.id === calculationMethod);

  return (
    <div 
      className={`min-h-screen font-sans selection:bg-purple-500 ${isRTL ? 'rtl' : 'ltr'}`}
      style={{ background: themeColors.pageBg }}
    >
      {/* Kids theme fun background */}
      {appTheme === 'kids' && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
          <div className="absolute top-10 left-5 text-4xl">⭐</div>
          <div className="absolute top-20 right-10 text-3xl">🌙</div>
          <div className="absolute top-40 left-20 text-2xl">✨</div>
          <div className="absolute bottom-40 right-5 text-4xl">🕋</div>
        </div>
      )}
      
      <PageHeader 
        title={t('prayerTrial.title')} 
        backPath="/prayer-times" 
        showProgressBar={false}
        extractedDate={mosqueResult?.extractedDate}
        hasExtractionData={mosqueResult?.success === true}
      />
      
      {/* Prayer Times Title */}
      <h1 className="text-2xl font-bold text-center pt-3 pb-1" style={{ color: themeColors.textOnPage }}>
        {t('prayerTrial.title')}
      </h1>

      {/* Discrete extracted-date subtitle - only when we have extraction data.
          Formatted client-side with Intl.DateTimeFormat so the date renders in
          the user's currently-selected app language (Arabic, French, Urdu,
          Bengali, etc.). Falls back to Gemini's English string if the ISO
          scheduleDate is unavailable. */}
      {(mosqueResult?.scheduleDate || mosqueResult?.extractedDate) && (
        <p
          className="text-center text-xs opacity-70 pb-3"
          style={{ color: themeColors.textOnPageSecondary }}
          data-testid="prayer-times-date"
        >
          {(() => {
            const iso = mosqueResult?.scheduleDate;
            if (iso) {
              try {
                const [y, m, d] = iso.split('-').map(Number);
                const dateObj = new Date(Date.UTC(y, m - 1, d));
                const locale = i18n.language || 'en';
                return new Intl.DateTimeFormat(locale, {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  timeZone: 'UTC',
                }).format(dateObj);
              } catch (e) {
                // fall through to English fallback below
              }
            }
            return mosqueResult?.extractedDate;
          })()}
        </p>
      )}

      {/* "Cached Schedule" pill - only when today's fetch failed and the backend
          served yesterday's row as a safety fallback. */}
      {mosqueResult?.stale && (
        <div className="flex justify-center pb-3">
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold"
            style={{
              background: 'rgba(201, 162, 39, 0.14)',
              color: '#8a6a10',
              border: '1px solid rgba(201, 162, 39, 0.45)',
            }}
            data-testid="cached-schedule-pill"
            title="Today's live fetch failed. Showing the last successfully cached schedule."
          >
            <span
              className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ background: '#c9a227' }}
            />
            {t('prayerTrial.cachedSchedule', 'Cached Schedule')}
          </span>
        </div>
      )}
      
      <div className="max-w-2xl mx-auto p-4 space-y-4 relative">
        
        {/* ================================================================= */}
        {/* PRAYER TIMETABLE */}
        {/* ================================================================= */}
        {displayTimes && (
          <div className="space-y-3" data-testid="prayer-timetable">
            {/* Refresh Button, Mosque Name (with dropdown), Q+ LIVE - all on same line */}
            <div className="flex items-center justify-between mb-2 gap-2">
              {/* Left side: Refresh button */}
              <Badge
                onClick={() => {
                  if (mode === 'mosque' && mosqueUrlInput) {
                    // Force refresh - bypass cache and re-extract
                    extractMosqueTimes(true);
                  } else if (mode === 'calculated' && location) {
                    window.location.reload();
                  }
                }}
                className="px-3 py-1 rounded-full flex items-center gap-1 cursor-pointer flex-shrink-0"
                style={{ background: themeColors.badgeBg, color: themeColors.badgeText }}
              >
                <RefreshCw className="w-4 h-4" />
                {t('prayerTrial.refresh', 'Refresh')}
              </Badge>
              
              {/* Middle: Mosque/Location name with dropdown for address */}
              <div className="flex-1 min-w-0">
                {mode === 'calculated' && locationName && (
                  <p className="text-xs text-center truncate" style={{ color: themeColors.textOnPageSecondary }}>
                    {locationName}
                  </p>
                )}
                {mode === 'mosque' && mosqueResult?.mosqueName && (
                  <details className="text-center">
                    <summary 
                      className="text-xs cursor-pointer list-none flex items-center justify-center gap-1"
                      style={{ color: themeColors.textOnPageSecondary }}
                    >
                      <span className="truncate">{mosqueResult.mosqueName}</span>
                      <ChevronDown className="w-3 h-3 flex-shrink-0" />
                    </summary>
                    {mosqueResult.address && (
                      <p className="text-[10px] mt-1 opacity-70" style={{ color: themeColors.textOnPageSecondary }}>
                        {mosqueResult.address}
                      </p>
                    )}
                  </details>
                )}
              </div>
              
              {/* Right side: Q+ LIVE badge */}
              <Badge 
                className="px-3 py-1 rounded-full flex items-center gap-1 flex-shrink-0"
                style={{ background: themeColors.badgeBg, color: themeColors.badgeText }}
              >
                <Zap className="w-4 h-4" />
                Q+ LIVE
              </Badge>
            </div>
            
            {/* Prayer Cards */}
            <div className="space-y-3">
              <PrayerCard 
                prayerName="fajr" 
                prayer={displayTimes.fajr} 
                isUpNext={upNextPrayer === 'fajr'}
                isCalculated={mode === 'calculated'}
                t={t}
                themeColors={themeColors} appTheme={appTheme} isDark={isDark}
              />
              
              {displayTimes.sunrise?.time && (
                <PrayerCard 
                  prayerName="sunrise" 
                  prayer={displayTimes.sunrise} 
                  isUpNext={upNextPrayer === 'sunrise'}
                  isCalculated={mode === 'calculated'}
                  t={t}
                  themeColors={themeColors} appTheme={appTheme} isDark={isDark}
                />
              )}
              
              <PrayerCard 
                prayerName="dhuhr" 
                prayer={displayTimes.dhuhr} 
                isUpNext={upNextPrayer === 'dhuhr'}
                isCalculated={mode === 'calculated'}
                t={t}
                themeColors={themeColors} appTheme={appTheme} isDark={isDark}
              />
              
              <PrayerCard 
                prayerName="asr" 
                prayer={displayTimes.asr} 
                isUpNext={upNextPrayer === 'asr'}
                isCalculated={mode === 'calculated'}
                t={t}
                themeColors={themeColors} appTheme={appTheme} isDark={isDark}
              />
              
              <PrayerCard 
                prayerName="maghrib" 
                prayer={displayTimes.maghrib} 
                isUpNext={upNextPrayer === 'maghrib'}
                isCalculated={mode === 'calculated'}
                t={t}
                themeColors={themeColors} appTheme={appTheme} isDark={isDark}
              />
              
              <PrayerCard 
                prayerName="isha" 
                prayer={displayTimes.isha} 
                isUpNext={upNextPrayer === 'isha'}
                isCalculated={mode === 'calculated'}
                t={t}
                themeColors={themeColors} appTheme={appTheme} isDark={isDark}
              />
              
              {/* Friday Prayer */}
              {mode === 'mosque' && displayTimes.jumuah && (
                <PrayerCard 
                  prayerName="jumuah" 
                  prayer={{ 
                    jamaah: typeof displayTimes.jumuah === 'string' 
                      ? displayTimes.jumuah 
                      : displayTimes.jumuah?.time || displayTimes.jumuah?.iqamah 
                  }} 
                  isFriday={true}
                  isCalculated={false}
                  t={t}
                  themeColors={themeColors} appTheme={appTheme} isDark={isDark}
                />
              )}
            </div>
          </div>
        )}

        {/* Loading state */}
        {loadingCalculated && !displayTimes && (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin" style={{ color: themeColors.accent }} />
          </div>
        )}

        {/* Refresh note for mosque mode - use textOnPageSecondary for Royal/Emerald dark backgrounds */}
        {mode === 'mosque' && (
          <p className="text-center text-sm py-2" style={{ color: themeColors.textOnPageSecondary }}>
            {t('prayerTrial.refreshNote')}
          </p>
        )}

        {/* ================================================================= */}
        {/* LOCATION & SETTINGS CARD */}
        {/* ================================================================= */}
        <div 
          className="tanzeen-card tanzeen-card-allow-overflow p-5 space-y-5"
        >
          {/* Corner decorations */}
          <div className="card-corner-tl" />
          <div className="card-corner-tr" />
          <div className="card-corner-bl" />
          <div className="card-corner-br" />
          
          {/* City Search */}
          <div className="flex gap-2 relative z-10">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#4a4a4a' }} />
              <Input
                placeholder={t('prayerTrial.searchCity')}
                value={citySearch}
                onChange={(e) => setCitySearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchCity()}
                className="pl-10 rounded-xl h-12"
                style={{ 
                  background: '#F1EBDD', 
                  borderColor: '#c9a227',
                  color: '#1a1a1a',
                }}
                data-testid="city-search-input"
              />
            </div>
            <Button 
              onClick={getCurrentLocation}
              disabled={loadingLocation}
              className="rounded-xl h-12 w-12 p-0"
              style={{ background: themeColors.accentGradient }}
              data-testid="gps-btn"
            >
              {loadingLocation ? (
                <RefreshCw className="w-5 h-5 animate-spin text-white" />
              ) : (
                <Navigation className="w-5 h-5 text-white" />
              )}
            </Button>
          </div>

          {/* Calculation Method Dropdown - Only show in calculated mode */}
          {/* z-index: dropdown wrapper elevated above sibling Smart Masjid Connect card
              (which uses z-10 internally) so the open dropdown never gets obscured. */}
          {mode === 'calculated' && (
            <div className={`space-y-2 relative ${showMethodDropdown ? 'z-40' : 'z-20'}`}>
              <label className="text-sm font-medium" style={{ color: '#1a1a1a' }}>{t('prayerTrial.calculationMethod')}</label>
              <div className="relative">
                <button
                  onClick={() => setShowMethodDropdown(!showMethodDropdown)}
                  className="w-full rounded-xl px-4 py-3 text-left flex items-center justify-between"
                  style={{ 
                    background: '#F1EBDD', 
                    borderColor: '#c9a227',
                    color: '#1a1a1a',
                    border: '1px solid #c9a227',
                  }}
                  data-testid="calculation-method-dropdown"
                >
                  <span>{selectedMethod?.name || t('prayerTrial.selectMethod')}</span>
                  <ChevronDown className={`w-5 h-5 transition-transform ${showMethodDropdown ? 'rotate-180' : ''}`} style={{ color: '#4a4a4a' }} />
                </button>
                
                {showMethodDropdown && (
                  <div 
                    className="absolute top-full left-0 right-0 mt-1 rounded-xl shadow-lg z-50 max-h-60 overflow-y-auto"
                    style={{ 
                      background: '#F1EBDD', 
                      border: '2px solid #c9a227',
                    }}
                    data-testid="calculation-method-list"
                  >
                    {CALCULATION_METHODS.map((method) => (
                      <button
                        key={method.id}
                        onClick={() => {
                          setCalculationMethod(method.id);
                          setShowMethodDropdown(false);
                        }}
                        className="w-full px-4 py-3 text-left border-b last:border-0 hover:opacity-80"
                        style={{ 
                          background: method.id === calculationMethod ? 'rgba(201, 162, 39, 0.2)' : 'transparent',
                          color: method.id === calculationMethod ? '#c9a227' : '#1a1a1a',
                          borderColor: themeColors.cardBorder,
                        }}
                      >
                        <p className="font-medium">{method.name}</p>
                        <p className="text-xs" style={{ color: themeColors.textSecondary }}>{method.description}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Refresh Button */}
          <Button
            onClick={mode === 'calculated' ? fetchCalculatedTimes : () => extractMosqueTimes(true)}
            disabled={loadingCalculated || mosqueExtracting}
            variant="outline"
            className="rounded-xl"
            style={{ 
              background: themeColors.inputBg, 
              borderColor: themeColors.cardBorder,
              color: themeColors.textPrimary,
            }}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${(loadingCalculated || mosqueExtracting) ? 'animate-spin' : ''}`} />
            {t('prayerTrial.refresh')}
          </Button>

          {/* ================================================================= */}
          {/* SMART MASJID CONNECT - Using Universal Cream Card Style */}
          {/* ================================================================= */}
          <div className="tanzeen-card relative overflow-hidden p-4 sm:p-6">
            {/* Corner decorations */}
            <div className="card-corner-tl" />
            <div className="card-corner-tr" />
            <div className="card-corner-bl" />
            <div className="card-corner-br" />
            
            <div className="relative flex flex-col sm:flex-row items-start gap-4 sm:gap-5 z-10">
              {/* Icon Container with gold border */}
              <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg" style={{
                border: '2px solid #c9a227',
                boxShadow: '0 0 20px rgba(16, 185, 129, 0.3)'
              }}>
                <Shield className="w-7 h-7 sm:w-8 sm:h-8 text-white drop-shadow-lg" />
              </div>
              
              <div className="flex-1 min-w-0 w-full">
                {/* Title with premium badge */}
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                  <h4 className="font-bold text-lg sm:text-xl tracking-wide" style={{ color: '#1a1a1a' }}>
                    {t('prayerTrial.intelligentExtraction')}
                  </h4>
                  <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-gradient-to-r from-amber-400 to-amber-500 text-amber-900 text-[10px] sm:text-[11px] font-bold rounded-full uppercase tracking-wider shadow-md whitespace-nowrap">
                    ✓ {t('prayerTrial.verified', 'Verified')}
                  </span>
                </div>
                
                {/* Description - dark black text */}
                <p className="text-sm sm:text-base leading-relaxed font-medium break-words pr-1" style={{
                  color: '#1a1a1a',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word'
                }}>
                  {t('prayerTrial.reliabilityGuarantee', "Very High Reliability Guarantee: Our system reads live mosque timetables directly. Can't find yours? Paste your Masjid's website link in the search bar below for high accurate prayer times.")}
                </p>
                
                {/* Trust indicators - same line, dark black text */}
                <div className="flex items-center gap-4 sm:gap-6 mt-3 sm:mt-4 pt-3 sm:pt-4" style={{ borderTop: '1px solid rgba(201, 162, 39, 0.3)' }}>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <div className="relative">
                      <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 bg-emerald-500 rounded-full animate-pulse" />
                      <div className="absolute inset-0 w-2 h-2 sm:w-2.5 sm:h-2.5 bg-emerald-500 rounded-full animate-ping opacity-75" />
                    </div>
                    <span className="text-xs sm:text-sm font-bold" style={{ color: '#1a1a1a' }}>{t('prayerTrial.liveSync', 'Live Sync')}</span>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-500" />
                    <span className="text-xs sm:text-sm font-bold" style={{ color: '#1a1a1a' }}>{t('prayerTrial.aiPowered', 'AI-Powered')}</span>
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500" />
                    <span className="text-xs sm:text-sm font-bold" style={{ color: '#1a1a1a' }}>{t('prayerTrial.accuracy100', 'Very High Accuracy')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div style={{ borderTop: `1px solid ${themeColors.cardBorder}` }} />

          {/* Local Mosque Times - PRO Feature - ID for hash navigation from Masjid Connect menu item */}
          <div id="masjid-connect" className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5" style={{ color: themeColors.accent }} />
              <span className="font-medium" style={{ color: themeColors.textPrimary }}>{t('prayerTrial.localMosqueTimes')}</span>
              <Badge className="text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1" style={{ background: themeColors.accent }}>
                <Crown className="w-3 h-3" />
                PRO
              </Badge>
            </div>
            <button
              onClick={handleModeToggle}
              className="font-medium text-sm"
              style={{ color: themeColors.accent }}
              data-testid="search-mosque-btn"
            >
              {mode === 'mosque' ? t('prayerTrial.useCalculated') : t('prayerTrial.searchMosque')}
            </button>
          </div>

          {/* ================================================================= */}
          {/* MOSQUE URL INPUT - Primary Search Method (RESTORED) */}
          {/* ================================================================= */}
          <div className="space-y-4 pt-2 relative">
            {/* PRO LOCK OVERLAY - Shows for non-Pro users */}
            {!hasMosqueAccess && (
              <div 
                className="absolute inset-0 z-10 rounded-xl flex flex-col items-center justify-center cursor-pointer"
                style={{ 
                  background: 'rgba(0, 0, 0, 0.8)',
                  backdropFilter: 'blur(4px)'
                }}
                onClick={() => requestFeatureAccess(PRO_FEATURES.MOSQUE_FINDER, () => {})}
                data-testid="mosque-url-pro-overlay"
              >
                <div className="rounded-full p-3 mb-3 shadow-lg" style={{ background: themeColors.accentGradient }}>
                  <Crown className="w-6 h-6 text-white" />
                </div>
                <p className="text-white font-semibold text-sm mb-1">{t('pro.proFeature') || 'Pro Feature'}</p>
                <p className="text-white/70 text-xs text-center px-4">{t('prayerTrial.unlockMosqueSearch') || 'Unlock Live Mosque Times'}</p>
                <div className="mt-3 px-4 py-2 rounded-full" style={{ background: themeColors.accentGradient }}>
                  <span className="text-white text-xs font-semibold flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    {t('pro.tapToUnlock') || 'Tap to Unlock'}
                  </span>
                </div>
              </div>
            )}
            
            <p className="text-sm" style={{ color: themeColors.textSecondary }}>
              {t('prayerTrial.enterMosqueUrlDesc') || 'Enter a mosque website URL OR postcode to find and load exact local Jama\'ah prayer times.'}
            </p>
            
            {/* Direct URL Input - THE PRIMARY SEARCH METHOD */}
            <div className="flex gap-2">
              <Input
                placeholder="mosque-website.com or postcode (e.g. E1 1BB)"
                value={mosqueUrlInput}
                onChange={(e) => handleMosqueUrlInputChange(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && hasMosqueAccess && handleMosqueInputSubmit()}
                disabled={!hasMosqueAccess}
                className="flex-1 rounded-xl h-12"
                style={{ 
                  background: appTheme === 'emerald' ? 'var(--emerald-elevated)' : themeColors.inputBg,
                  borderColor: themeColors.inputBorder,
                  color: appTheme === 'emerald' ? 'var(--emerald-ivory)' : themeColors.textPrimary,
                  WebkitTextFillColor: appTheme === 'emerald' ? 'var(--emerald-ivory)' : themeColors.textPrimary,
                }}
                data-testid="mosque-url-input"
              />
              <Button 
                onClick={() => hasMosqueAccess ? handleMosqueInputSubmit() : requestFeatureAccess(PRO_FEATURES.MOSQUE_FINDER, () => {})}
                disabled={mosqueExtracting || findingNearbyMosque || (hasMosqueAccess && !mosqueUrlInput.trim())}
                className="rounded-xl h-12 px-4 text-white"
                style={{ background: themeColors.accentGradient }}
                data-testid="extract-mosque-btn"
              >
                {mosqueExtracting ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : hasMosqueAccess ? (
                  <Radar className="w-5 h-5" />
                ) : (
                  <Lock className="w-5 h-5" />
                )}
              </Button>
            </div>
            <p
              className="text-xs mt-2"
              style={{ color: themeColors.textSecondary }}
              data-testid="mosque-discovery-usage-indicator"
            >
              {mosqueSearchRemaining}/5 mosque searches remaining today
            </p>
            
            {/* Mosque Finder Assist - Postcode search in progress */}
            {findingNearbyMosque && (
              <div 
                className="rounded-xl p-3 flex items-center gap-3"
                style={{ 
                  background: `${themeColors.accent}15`, 
                  border: `1px solid ${themeColors.accent}40`,
                }}
              >
                <Search className="w-5 h-5 animate-pulse" style={{ color: themeColors.accent }} />
                <div>
                  <p className="text-sm font-medium" style={{ color: themeColors.textPrimary }}>{t('prayerTrial.findingMosque') || 'Finding nearby mosque...'}</p>
                  <p className="text-xs" style={{ color: themeColors.textSecondary }}>{t('prayerTrial.willAutofillUrl') || 'Will auto-fill the website URL'}</p>
                </div>
              </div>
            )}

            {/* Extraction in progress */}
            {mosqueExtracting && (
              <div 
                className="rounded-xl p-3 flex items-center gap-3"
                style={{ 
                  background: `${themeColors.accent}15`, 
                  border: `1px solid ${themeColors.accent}40`,
                }}
              >
                <RefreshCw className="w-5 h-5 animate-spin" style={{ color: themeColors.accent }} />
                <div>
                  <p className="text-sm font-medium" style={{ color: themeColors.textPrimary }}>{t('prayerTrial.analyzing')}</p>
                  <p className="text-xs" style={{ color: themeColors.textSecondary }}>{t('prayerTrial.mayTake60Seconds')}</p>
                </div>
              </div>
            )}
            
            {/* Error message */}
            {mosqueResult && !mosqueResult.success && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">{t('prayerTrial.extractionFailed')}</p>
                  <p className="text-xs text-red-600">{mosqueResult.error}</p>
                </div>
              </div>
            )}
            
            {/* Success info with Save button */}
            {mosqueResult?.success && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-green-800">
                        {mosqueResult.dataSource?.includes('mawaqit') 
                          ? t('prayerTrial.verifiedDataRetrieved') 
                          : t('prayerTrial.extractionSuccessful')}
                      </p>
                      <p className="text-xs text-green-600">
                        {mosqueResult.extractedCount} {t('prayerTrial.prayerTimesFound')}
                        {usingCachedData && ` (${t('prayerTrial.cached')})`}
                      </p>
                      {/* Ad-user notice: Manual extraction only, no auto-refresh */}
                      {!isPro && (
                        <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                          <Crown className="w-3 h-3" />
                          {t('prayerTrial.proAutoRefreshNotice', 'Upgrade to Pro for automatic daily updates')}
                        </p>
                      )}
                    </div>
                  </div>
                  {/* Save to My Masjids button - visible for all users */}
                  {!savedMasjids.some(m => m.masjid_name === mosqueResult.mosqueName) && (
                    <Button
                      onClick={saveMasjidToFavorites}
                      disabled={savingMasjid}
                      variant="ghost"
                      size="sm"
                      className="text-green-700 hover:bg-green-100"
                      data-testid="save-masjid-btn"
                    >
                      {savingMasjid ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Crown className="w-4 h-4 mr-1" />
                          {t('prayerTrial.saveToMyMasjids') || 'Save'}
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* ================================================================= */}
        {/* MY MASJIDS - PRO Feature (MongoDB backed) */}
        {/* ================================================================= */}
        <div 
          className="rounded-3xl p-5 space-y-4"
          style={{ 
            background: themeColors.cardBg, 
            border: `1px solid ${themeColors.cardBorder}`,
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5" style={{ color: themeColors.accent }} />
              <h3 className="text-lg font-semibold card-title-dark" style={{ color: themeColors.textPrimary }}>{t('prayerTrial.myMasjids') || 'My Masjids'}</h3>
            </div>
            {savedMasjids.length > 0 && (
              <span className="text-xs" style={{ color: themeColors.textSecondary }}>{savedMasjids.length}/10</span>
            )}
          </div>
          
          {/* My Masjids Content - No PRO lock, just show empty state if no saved masjids */}
          <div data-testid="my-masjids-section">
            {loadingSavedMasjids ? (
              <div className="flex items-center justify-center py-6">
                <RefreshCw className="w-6 h-6 animate-spin" style={{ color: themeColors.accent }} />
              </div>
            ) : savedMasjids.length === 0 ? (
              <div className="text-center py-6" style={{ color: themeColors.textSecondary }}>
                <Building2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">{t('prayerTrial.noSavedMasjids') || 'No saved masjids yet'}</p>
                <p className="text-xs mt-1">{t('prayerTrial.searchAndSave') || 'Search for a mosque above and tap Save'}</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {savedMasjids.map((masjid) => (
                  <div 
                    key={masjid.masjid_url}
                    className="relative rounded-xl p-3 shadow-sm cursor-pointer hover:shadow-md transition-all group"
                      style={{ 
                        background: themeColors.inputBg,
                        border: `1px solid ${themeColors.cardBorder}`,
                      }}
                      onClick={() => loadSavedMasjid(masjid)}
                      data-testid={`saved-masjid-${masjid.masjid_name?.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: themeColors.accentGradient }}>
                          <Building2 className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate" style={{ color: themeColors.textPrimary }}>{masjid.masjid_name}</p>
                          <p className="text-xs truncate" style={{ color: themeColors.textSecondary }}>{masjid.address || t('prayerTrial.tapToLoad') || 'Tap to load times'}</p>
                        </div>
                      </div>
                      
                      {/* Remove button (visible on hover) */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeMasjidFromFavorites(masjid.masjid_url);
                        }}
                        className="absolute top-1 right-1 p-1 rounded-full bg-red-100 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`remove-masjid-${masjid.masjid_name?.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          
          {/* Pro Benefits Summary */}
          <div 
            className="rounded-xl p-3"
            style={{ 
              background: themeColors.badgeBg,
              border: `1px solid ${themeColors.cardBorder}`,
            }}
          >
            <div className="flex items-center gap-2 text-xs" style={{ color: themeColors.accent }}>
              <Zap className="w-4 h-4" />
              <span className="font-medium">{t('prayerTrial.proTip') || 'Pro Tip'}:</span>
              <span style={{ color: themeColors.textSecondary }}>{t('prayerTrial.savedMasjidsSkipSearch') || 'Saved masjids let you skip the search step!'}</span>
            </div>
          </div>
          
          {/* Last Updated Footer - Cache-Buster Verification */}
          {lastUpdated && (
            <div 
              className="mt-4 py-3 px-4 rounded-xl text-center"
              style={{ 
                background: 'rgba(201, 162, 39, 0.1)',
                border: '1px solid rgba(201, 162, 39, 0.2)',
              }}
            >
              <div className="flex items-center justify-center gap-2 text-xs" style={{ color: '#4a4a4a' }}>
                <Clock className="w-3.5 h-3.5" style={{ color: '#c9a227' }} />
                <span className="font-medium" style={{ color: '#1a1a1a' }}>
                  {t('prayerTrial.lastUpdated', 'Last Updated')}:
                </span>
                <span>
                  {(() => {
                    const date = new Date(lastUpdated);
                    const today = new Date();
                    const isToday = date.toDateString() === today.toDateString();
                    const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                    if (isToday) {
                      return `${t('prayerTrial.today', 'Today')} ${time}`;
                    }
                    return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + time;
                  })()}
                </span>
                {usingCachedData && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ background: 'rgba(201, 162, 39, 0.2)', color: '#c9a227' }}>
                    {t('prayerTrial.cached', 'Cached')}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrayerTrialPage;
